import { useState, useCallback, useEffect, useRef } from "react";
import {
  LayoutDashboard, Users, FolderKanban, TrendingUp, Mail, Phone, Calendar,
  Search, Bell, Plus, ArrowUpRight, ArrowDownRight, Target, Send,
  Briefcase, FileText, Activity, ChevronLeft, Menu, LogOut,
  Linkedin, MessageSquare, X, DollarSign, Settings,
  CheckSquare, Square, Clock
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell,
} from "recharts";
import { PIPELINE_STAGES, INDUSTRIES } from "./data/prospects";
import { useProspects } from "./data/useProspects";
import { useActivities } from "./data/useActivitiesFirestore";
import { useTeamMembers } from "./data/useTeamMembers";
import { useAuth } from "./auth/AuthContext";

// ─── Status normalizer — maps raw CRM export values → our pipeline keys ───────
const STATUS_ALIASES = {
  // → identified
  lead: "identified", new: "identified", prospect: "identified",
  open: "identified", mql: "identified", unqualified: "identified",
  initiated: "identified", researching: "identified", "ready to reach out": "identified",
  "open - not contacted": "identified", "not contacted": "identified",
  "new lead": "identified",
  // → contacted
  contacted: "contacted", working: "contacted", sql: "contacted",
  connected: "contacted", "attempting to contact": "contacted",
  "attempted to contact": "contacted", "in progress": "contacted",
  "working - contacted": "contacted", "open deal": "contacted",
  // → discovery
  discovery: "discovery", "discovery call": "discovery",
  "meeting scheduled": "discovery", qualified: "discovery",
  "demo scheduled": "discovery", "decision maker bought-in": "discovery",
  // → proposal
  proposal: "proposal", "proposal sent": "proposal",
  negotiation: "proposal", "contract sent": "proposal",
  // → closed
  closed: "closed", "closed won": "closed", won: "closed",
  customer: "closed", "closed - converted": "closed",
};
function normalizeStatus(raw) {
  if (!raw) return "identified";
  const key = raw.toLowerCase().trim();
  return STATUS_ALIASES[key] || (["identified","contacted","discovery","proposal","closed"].includes(key) ? key : "identified");
}


function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [breakpoint]);
  return isMobile;
}

// ─── Brand Tokens ─────────────────────────────────────────────────────────────
const B = {
  purple: "#715FF2", purpleLight: "#8B7CF5", purplePale: "#F0EEFE",
  purpleDark: "#5A4AD0", black: "#070626", lime: "#CBFF00", limeDark: "#A8D600",
  white: "#FFFFFF", gray50: "#FAFAFE", gray100: "#F4F3F9", gray200: "#E8E7F0",
  gray300: "#D1D0DC", gray400: "#9896A8", gray500: "#6E6C80", gray600: "#4A4860",
  warning: "#F29C06", warningBg: "#FFF8E6", danger: "#DF1C41", dangerBg: "#FFF0F3",
  success: "#10B981", successBg: "#ECFDF5",
  sidebar: "#0D0B2E", sidebarBorder: "rgba(255,255,255,0.07)",
};

// ─── Service Types ────────────────────────────────────────────────────────────
const SERVICE_TYPES = [
  {
    key: "assessment",
    name: "AI Readiness Assessment",
    engagementType: "One-Time Engagement",
    description: "AI system inventory & discovery, data governance gap analysis, risk surface mapping, regulatory alignment review, and 90-day roadmap.",
    icon: FileText,
    color: B.purple,
    colorBg: B.purplePale,
  },
  {
    key: "managed",
    name: "Managed AI Governance",
    engagementType: "Ongoing Retainer",
    description: "Dedicated governance advisor, AI policy development & maintenance, vendor AI risk assessments, employee training, and audit-ready reporting.",
    icon: Briefcase,
    color: B.success,
    colorBg: B.successBg,
    popular: true,
  },
  {
    key: "operations",
    name: "AI Governance Operations",
    engagementType: "Full-Service Model",
    description: "AI agent deployment governance, continuous compliance monitoring, regulatory change tracking, role-specific enablement, and board-ready reporting.",
    icon: Activity,
    color: B.warning,
    colorBg: B.warningBg,
  },
];

// ─── Outreach Email Templates ─────────────────────────────────────────────────
const DEFAULT_TEMPLATES = [
  { id: "initial",       name: "Initial Outreach",   touchNumber: 1 },
  { id: "followup",      name: "Follow-Up",           touchNumber: 2 },
  { id: "finalask",      name: "Gentle Nudge",        touchNumber: 4 },
  { id: "breakup",       name: "Closing Note",        touchNumber: 5 },
  { id: "discovery",     name: "Discovery Confirmed", touchNumber: null },
  { id: "postdiscovery", name: "Post-Discovery",      touchNumber: null },
];

// Stage → subject line
const STAGE_SUBJECTS = {
  initial:       "AI governance at {{company}}",
  followup:      "Re: AI governance at {{company}}",
  finalask:      "Checking in — {{company}}",
  breakup:       "Keeping the door open — {{company}}",
  discovery:     "Looking forward to our call",
  postdiscovery: "Next steps for {{company}}",
};

// Title group detection
function getTitleGroup(title) {
  const t = (title || "").toLowerCase();
  if (["ciso", "chief information security", "director of information security", "vp information security"]
    .some(k => t.includes(k))) return "A";
  if (["chief compliance", "vp compliance", "head of compliance", "director of compliance"]
    .some(k => t.includes(k))) return "B";
  if (["grc", "vp risk", "head of risk", "director of risk", "ai governance", "head of ai"]
    .some(k => t.includes(k))) return "C";
  return "B"; // default fallback
}

// 12 body combinations — [RESEARCH HOOK] replaced at compose time, {{sig}}/{{shortSig}} replaced with sender sig
const GROUP_BODIES = {
  A: {
    initial:
`Hi {{firstName}},

[RESEARCH HOOK]

As CISO, you likely own the question of whether those systems are documented, owned, and audit-ready against this year's regulatory requirements.

We help security leaders close that gap in about 5 weeks.

Would this be worth 20 minutes?

{{sig}}`,
    followup:
`Hi {{firstName}},

Wanted to follow up on my previous note.

CISOs I speak with are increasingly finding that AI governance is landing in their scope alongside traditional security risk, especially as regulators start expecting documented controls rather than policy statements.

If the timing is off, happy to reconnect later. Otherwise, still worth a quick conversation?

{{shortSig}}`,
    discovery:
`Hi {{firstName}},

Looking forward to our conversation.

I will come prepared with a brief overview of how our approach applies to organizations at {{company}}'s stage and security posture. If there is anything specific you would like to focus on, feel free to send it over beforehand.

Talk soon.

{{sig}}`,
    postdiscovery:
`Hi {{firstName}},

Thank you for your time today. The conversation was helpful in understanding where {{company}} is and what matters most.

Based on what we discussed, I will put together a tailored proposal that addresses [KEY PAIN - fill in immediately after call]. You can expect it by [DATE].

In the meantime, feel free to reach out with any questions.

{{shortSig}}`,
    finalask:
`Hi {{firstName}},

I have sent a few notes and want to be mindful of your time, so I will keep this brief.

AI governance is moving quickly on the regulatory side, and CISOs who have visibility into their AI stack early are in a much better position when the questions start coming from the board or auditors.

If there is ever a good time to connect, I am easy to reach.

{{shortSig}}`,
    breakup:
`Hi {{firstName}},

I wanted to send one final note before I stop following up.

If AI governance at {{company}} becomes a priority down the road, please do not hesitate to reach out. I would be glad to pick up the conversation whenever the timing works for you.

Wishing you and the team well.

{{shortSig}}`,
  },
  B: {
    initial:
`Hi {{firstName}},

[RESEARCH HOOK]

At that stage, the question most CCOs face is whether AI use across the business is inventoried, risk-classified, and defensible to regulators.

We help compliance leaders close that gap in about 5 weeks.

Would this be worth 20 minutes?

{{sig}}`,
    followup:
`Hi {{firstName}},

Wanted to follow up on my previous note.

The compliance leaders getting ahead of this now are the ones building their AI inventory and risk classification before regulators come asking. The ones waiting are building it under pressure.

If this is not a priority right now, completely understood. Otherwise, still worth 20 minutes?

{{shortSig}}`,
    discovery:
`Hi {{firstName}},

Looking forward to our conversation.

I will come prepared with a brief overview of how we approach AI governance for compliance-led organizations. If there is a specific regulatory requirement or use case you want to prioritize, feel free to flag it beforehand.

Talk soon.

{{sig}}`,
    postdiscovery:
`Hi {{firstName}},

Thank you for your time today. It was valuable hearing where {{company}} stands on the compliance side.

Based on what we covered, I will put together a proposal focused on [KEY PAIN - fill in immediately after call]. You can expect it by [DATE].

Reach out if anything comes up in the meantime.

{{shortSig}}`,
    finalask:
`Hi {{firstName}},

I have reached out a few times and want to respect your inbox, so I will keep this short.

Regulatory pressure around AI is building and compliance teams that have an inventory and risk classification in place early are in a far stronger position. If that conversation would be useful at {{company}}, I am happy to connect at your convenience.

{{shortSig}}`,
    breakup:
`Hi {{firstName}},

I wanted to send a final note before stepping back from following up.

If AI governance or compliance readiness becomes a focus at {{company}}, please feel free to reach out. I would genuinely enjoy the conversation whenever the time is right.

Wishing you and the team well.

{{shortSig}}`,
  },
  C: {
    initial:
`Hi {{firstName}},

[RESEARCH HOOK]

At that pace, the question most GRC and risk leaders face is whether AI systems across the organization are mapped, owned, and controlled against the frameworks you are already managing.

We help teams close that gap in about 5 weeks.

Would this be worth 20 minutes?

{{sig}}`,
    followup:
`Hi {{firstName}},

Wanted to follow up on my previous note.

Most GRC teams I work with already have the frameworks. What is missing is visibility into the AI systems those frameworks are supposed to govern. It is a gap that is hard to close without dedicated focus.

If timing is not right, no problem. Otherwise, still worth a conversation?

{{shortSig}}`,
    discovery:
`Hi {{firstName}},

Looking forward to our conversation.

I will come prepared with context on how we help GRC teams build AI governance into their existing control frameworks. If there is a specific framework or risk area you want to focus on, feel free to send it over.

Talk soon.

{{sig}}`,
    postdiscovery:
`Hi {{firstName}},

Thank you for your time today. The context on {{company}}'s current GRC environment was helpful.

I will put together a proposal that maps to [KEY PAIN - fill in immediately after call] and your existing framework. Expect it by [DATE].

Feel free to reach out with any questions before then.

{{shortSig}}`,
    finalask:
`Hi {{firstName}},

I have sent a few notes and want to be considerate of your time, so I will leave it here for now.

GRC and risk teams that get ahead of AI governance tend to find it much easier to integrate into their existing frameworks before there is external pressure to do so. If that kind of conversation would be valuable at {{company}}, I am always happy to connect.

{{shortSig}}`,
    breakup:
`Hi {{firstName}},

I wanted to send one last note before stepping back.

If AI governance or risk framework work becomes a priority at {{company}}, please do not hesitate to get in touch. I would be glad to reconnect whenever the timing is right for you.

Wishing you and the team well.

{{shortSig}}`,
  },
};

// ─── 5-Touch Sequence ────────────────────────────────────────────────────────
const DEFAULT_TOUCH_SEQUENCE = [
  { touch: 1, label: "Initial Outreach",  channel: "email",    dayOffset: 0,  templateId: "initial"  },
  { touch: 2, label: "Follow-Up",          channel: "email",    dayOffset: 5,  templateId: "followup" },
  { touch: 3, label: "Value Touch",        channel: "linkedin", dayOffset: 12, templateId: null       },
  { touch: 4, label: "Gentle Nudge",        channel: "email",    dayOffset: 21, templateId: "finalask" },
  { touch: 5, label: "Closing Note",       channel: "email",    dayOffset: 30, templateId: "breakup"  },
];

function getSequence(p) {
  return (p.touchSequence && p.touchSequence.length === 5) ? p.touchSequence : DEFAULT_TOUCH_SEQUENCE;
}

function getCurrentTouch(p) {
  if (p.nurture) return null;
  const seq = getSequence(p);
  const num = p.touchNumber || 1;
  if (num > 5) return null;
  return seq.find(t => t.touch === num) || seq[0];
}

function isFollowUpDue(p) {
  if (p.nurture) return false;
  if (!["identified", "contacted"].includes(p.status)) return false;
  // Legacy: manually-set nextFollowUp date (pre-sequence system)
  // Require lastContact to be set — "initiated" prospects with no contact history are excluded
  if (p.nextFollowUp && !p.lastTouchSentAt) {
    if (!p.lastContact && p.status !== "contacted") return false;
    const today = new Date(); today.setHours(23, 59, 59, 999);
    return new Date(p.nextFollowUp) <= today;
  }
  // New sequence: only show if explicitly started via handleMarkTouchSent
  if (!p.lastTouchSentAt || !p.touchNumber || p.touchNumber < 2) return false;
  const touch = getCurrentTouch(p);
  if (!touch) return false;
  // T1 sentDate is our base; fall back to lastTouchSentAt only if missing
  const t1SentDate = p.touchSequence?.[0]?.sentDate || p.lastTouchSentAt;
  const dueDate = new Date(t1SentDate);
  dueDate.setDate(dueDate.getDate() + touch.dayOffset);
  const today = new Date(); today.setHours(23, 59, 59, 999);
  return dueDate <= today;
}

function getDaysOverdue(p) {
  const touch = getCurrentTouch(p);
  if (!touch) return null;
  const lastSent = p.lastTouchSentAt;
  if (!lastSent) return null;
  const base = new Date(p.touchSequence?.[0]?.sentDate || lastSent);
  const dueDate = new Date(base);
  dueDate.setDate(dueDate.getDate() + touch.dayOffset);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.floor((today - dueDate) / 86400000);
  return diff > 0 ? diff : null;
}

// ─── CSV parser (handles quoted fields with commas inside) ────────────────────
function parseCSVLine(line) {
  const cells = [];
  let cur = "", inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
      else inQ = !inQ;
    } else if (ch === ',' && !inQ) { cells.push(cur.trim()); cur = ""; }
    else cur += ch;
  }
  cells.push(cur.trim());
  return cells;
}

// Smart column detector: exact → partial (header contains keyword) → content sniff
function detectCol(headers, sampleRows, keywords, contentTest) {
  // 1. Exact match
  for (const kw of keywords) {
    const i = headers.indexOf(kw);
    if (i !== -1) return i;
  }
  // 2. Partial match — header contains keyword
  for (const kw of keywords) {
    const i = headers.findIndex(h => h.includes(kw));
    if (i !== -1) return i;
  }
  // 3. Content sniff — score each column by how many sample rows pass the test
  if (contentTest) {
    const scores = headers.map((_, i) => sampleRows.filter(r => r[i] && contentTest(r[i])).length);
    const max = Math.max(...scores);
    if (max >= 1) return scores.indexOf(max);
  }
  return -1;
}

// ─── Shared UI components ─────────────────────────────────────────────────────
const PriorityBadge = ({ priority }) => {
  const map = {
    high: { bg: B.dangerBg, color: B.danger, label: "HIGH" },
    medium: { bg: B.warningBg, color: B.warning, label: "MED" },
    low: { bg: B.successBg, color: B.success, label: "LOW" },
  };
  const s = map[priority] || map.low;
  return <span style={{ background: s.bg, color: s.color, padding: "2px 8px", borderRadius: 5, fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>{s.label}</span>;
};

const StatusBadge = ({ status }) => {
  const map = {
    identified: { bg: B.gray100, color: B.gray500, label: "Identified" },
    contacted: { bg: B.purplePale, color: B.purple, label: "Contacted" },
    discovery: { bg: B.warningBg, color: B.warning, label: "Discovery" },
    proposal: { bg: B.successBg, color: B.success, label: "Proposal" },
    closed: { bg: "#EFFFCC", color: B.limeDark, label: "Closed Won" },
  };
  const s = map[status] || map.identified;
  return <span style={{ background: s.bg, color: s.color, padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>{s.label}</span>;
};

const TouchBadge = ({ prospect }) => {
  const touch = getCurrentTouch(prospect);
  if (!touch && !prospect.nurture) return null;
  if (!prospect.nurture && !prospect.lastTouchSentAt && prospect.status !== "contacted") return null;
  if (prospect.nurture) return (
    <span style={{ background: B.gray100, color: B.gray500, padding: "2px 7px", borderRadius: 5, fontSize: 10, fontWeight: 700, letterSpacing: 0.3, whiteSpace: "nowrap" }}>Nurture</span>
  );
  const colors = touch.touch >= 4 ? [B.dangerBg, B.danger] : touch.touch === 3 ? [B.warningBg, B.warning] : [B.purplePale, B.purple];
  return (
    <span style={{ background: colors[0], color: colors[1], padding: "2px 7px", borderRadius: 5, fontSize: 10, fontWeight: 700, letterSpacing: 0.3, whiteSpace: "nowrap" }}>
      T{touch.touch}/5
    </span>
  );
};

const MetricCard = ({ icon: Icon, label, value, change, changeType, prefix, suffix, lime }) => (
  <div style={{
    background: lime ? B.lime : B.white, borderRadius: 14, padding: "22px 24px",
    border: `1px solid ${lime ? "transparent" : B.gray200}`, flex: 1, minWidth: 180,
    transition: "box-shadow 0.2s, transform 0.15s", cursor: "default",
  }}
    onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 8px 24px rgba(113,95,242,0.1)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
    onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}
  >
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: lime ? "rgba(0,0,0,0.08)" : B.purplePale, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon size={18} color={lime ? B.black : B.purple} />
      </div>
      {change !== undefined && (
        <div style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 12, fontWeight: 600, color: changeType === "up" ? B.success : changeType === "down" ? B.danger : B.gray400 }}>
          {changeType === "up" ? <ArrowUpRight size={14} /> : changeType === "down" ? <ArrowDownRight size={14} /> : null}
          {change}
        </div>
      )}
    </div>
    <div style={{ marginTop: 16, fontSize: 26, fontWeight: 700, color: B.black, fontFamily: "'Poppins', sans-serif", letterSpacing: -0.5 }}>
      {prefix}{value}{suffix}
    </div>
    <div style={{ fontSize: 12, color: lime ? "rgba(0,0,0,0.6)" : B.gray500, marginTop: 4, fontWeight: 500 }}>{label}</div>
  </div>
);

const ProgressBar = ({ value, height = 6, color = B.purple }) => (
  <div style={{ width: "100%", height, borderRadius: height, background: B.gray200, overflow: "hidden" }}>
    <div style={{ width: `${Math.min(value, 100)}%`, height: "100%", borderRadius: height, background: color, transition: "width 0.6s ease" }} />
  </div>
);

const inputStyle = {
  width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${B.gray200}`,
  fontSize: 13, outline: "none", fontFamily: "inherit", background: B.white, boxSizing: "border-box",
};

const labelStyle = {
  fontSize: 11, fontWeight: 700, color: B.gray500, textTransform: "uppercase",
  letterSpacing: 0.5, display: "block", marginBottom: 6,
};

// ─── Activity + Edit Modal ────────────────────────────────────────────────────
const ProspectModal = ({ prospect, onClose, getActivities, addActivity, deleteActivity, user, onUpdate }) => {
  const [actType, setActType] = useState("email");
  const [actNote, setActNote] = useState("");
  const [editEmail, setEditEmail] = useState(prospect.email || "");
  const [editPhone, setEditPhone] = useState(prospect.phone || "");
  const [editDealValue, setEditDealValue] = useState(prospect.dealValue ? String(prospect.dealValue) : "");
  const [editStatus, setEditStatus] = useState(prospect.status);
  const [editServiceType, setEditServiceType] = useState(prospect.serviceType || "");
  const [editNotes, setEditNotes] = useState(prospect.notes || "");
  const [editNextFollowUp, setEditNextFollowUp] = useState(prospect.nextFollowUp ? new Date(prospect.nextFollowUp).toISOString().split("T")[0] : "");
  const [tab, setTab] = useState("activity"); // "activity" | "edit"

  const acts = getActivities(prospect.id);

  const handleLogActivity = () => {
    if (!actNote.trim()) return;
    addActivity(prospect.id, { type: actType, note: actNote.trim(), user: user?.name || "Sales Team" });
    setActNote("");
  };

  const handleSaveEdits = () => {
    const updates = {
      email: editEmail.trim() || null,
      phone: editPhone.trim() || null,
      dealValue: editDealValue ? parseFloat(editDealValue.replace(/[^0-9.]/g, "")) : null,
      status: editStatus,
      serviceType: editServiceType || null,
      notes: editNotes.trim(),
      nextFollowUp: editNextFollowUp ? new Date(editNextFollowUp).toISOString() : null,
    };
    // Auto-set firstContact date when moving to contacted
    if (editStatus !== "identified" && !prospect.firstContact) {
      updates.firstContact = new Date().toLocaleDateString("en-US");
    }
    onUpdate(prospect.id, updates);
    onClose();
  };

  const typeIcons = { email: Mail, call: Phone, meeting: Calendar, note: FileText, linkedin: Linkedin };
  const typeColors = { email: B.purple, call: B.success, meeting: B.warning, note: B.gray500, linkedin: "#0A66C2" };
  const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(7,6,38,0.7)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(4px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ background: B.white, borderRadius: 18, width: "100%", maxWidth: 540, maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 64px rgba(7,6,38,0.3)" }}>

        {/* Header */}
        <div style={{ padding: "24px 28px 16px", borderBottom: `1px solid ${B.gray200}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <PriorityBadge priority={prospect.priority} />
              <StatusBadge status={editStatus} />
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: B.black, fontFamily: "'Poppins', sans-serif" }}>{prospect.name}</div>
            <div style={{ fontSize: 12, color: B.gray500, marginTop: 2 }}>{prospect.title} · {prospect.company}</div>
          </div>
          <button onClick={onClose} style={{ background: B.gray100, border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={16} color={B.gray500} />
          </button>
        </div>

        {/* Quick actions */}
        <div style={{ padding: "12px 28px", borderBottom: `1px solid ${B.gray200}`, display: "flex", gap: 8, flexWrap: "wrap" }}>
          {(editEmail || prospect.email) && (
            <a href={`https://mail.google.com/mail/?view=cm&fs=1&to=${editEmail || prospect.email}&su=Revoya Advisory - ${prospect.company}`}
              target="_blank" rel="noreferrer"
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", background: B.purplePale, color: B.purple, borderRadius: 8, fontSize: 12, fontWeight: 600, textDecoration: "none" }}>
              <Mail size={13} /> Email
            </a>
          )}
          {prospect.linkedin && (
            <a href={prospect.linkedin} target="_blank" rel="noreferrer"
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", background: "#EBF5FF", color: "#0A66C2", borderRadius: 8, fontSize: 12, fontWeight: 600, textDecoration: "none" }}>
              <Linkedin size={13} /> LinkedIn
            </a>
          )}
          {(editPhone || prospect.phone) && (
            <a href={`tel:${editPhone || prospect.phone}`}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", background: B.successBg, color: B.success, borderRadius: 8, fontSize: 12, fontWeight: 600, textDecoration: "none" }}>
              <Phone size={13} /> {editPhone || prospect.phone}
            </a>
          )}
        </div>

        {/* Tab switcher */}
        <div style={{ display: "flex", gap: 0, padding: "0 28px", borderBottom: `1px solid ${B.gray200}` }}>
          {["activity", "edit"].map((t) => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: "10px 16px", fontSize: 13, fontWeight: 600, border: "none", background: "none",
              cursor: "pointer", textTransform: "capitalize",
              color: tab === t ? B.purple : B.gray400,
              borderBottom: `2px solid ${tab === t ? B.purple : "transparent"}`,
              marginBottom: -1,
            }}>{t === "activity" ? `Activity Log (${acts.length})` : "Edit Details"}</button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {tab === "activity" ? (
            <div style={{ padding: "20px 28px 24px" }}>
              {/* Log form */}
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Log New Activity</label>
                <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                  {["email", "call", "meeting", "note", "linkedin"].map((t) => (
                    <button key={t} onClick={() => setActType(t)} style={{
                      padding: "5px 12px", borderRadius: 7, fontSize: 11, fontWeight: 600,
                      border: "none", cursor: "pointer", textTransform: "capitalize",
                      background: actType === t ? B.purple : B.gray100,
                      color: actType === t ? B.white : B.gray500,
                    }}>{t}</button>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input value={actNote} onChange={(e) => setActNote(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleLogActivity()}
                    placeholder={`Note about this ${actType}…`}
                    style={{ ...inputStyle, flex: 1 }} />
                  <button onClick={handleLogActivity} disabled={!actNote.trim()} style={{
                    padding: "9px 16px", borderRadius: 8, fontSize: 12, fontWeight: 600, border: "none",
                    cursor: actNote.trim() ? "pointer" : "not-allowed",
                    background: actNote.trim() ? B.purple : B.gray200,
                    color: actNote.trim() ? B.white : B.gray400,
                  }}>Save</button>
                </div>
              </div>

              {/* History */}
              <label style={labelStyle}>History</label>
              {acts.length === 0 ? (
                <div style={{ textAlign: "center", color: B.gray400, fontSize: 13, paddingTop: 16 }}>No activities yet — log the first one above.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {acts.map((act) => {
                    const Icon = typeIcons[act.type] || MessageSquare;
                    return (
                      <div key={act.id} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: B.gray100, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <Icon size={14} color={typeColors[act.type] || B.gray500} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: B.black, textTransform: "capitalize" }}>{act.type}</span>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontSize: 11, color: B.gray400 }}>{fmtDate(act.date)}</span>
                              <button onClick={() => deleteActivity(prospect.id, act.id)} title="Delete" style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex", alignItems: "center", borderRadius: 4, opacity: 0.4 }}
                                onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.background = B.dangerBg; }}
                                onMouseLeave={e => { e.currentTarget.style.opacity = "0.4"; e.currentTarget.style.background = "none"; }}>
                                <X size={12} color={B.danger} />
                              </button>
                            </div>
                          </div>
                          <div style={{ fontSize: 13, color: B.gray600, marginTop: 2 }}>{act.note}</div>
                          <div style={{ fontSize: 11, color: B.gray400, marginTop: 2 }}>by {act.user}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: "20px 28px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
              {/* Status */}
              <div>
                <label style={labelStyle}>Pipeline Stage</label>
                <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)}
                  style={{ ...inputStyle, cursor: "pointer" }}>
                  {PIPELINE_STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>

              {/* Email */}
              <div>
                <label style={labelStyle}>Email Address</label>
                <input value={editEmail} onChange={(e) => setEditEmail(e.target.value)}
                  placeholder="email@company.com" type="email" style={inputStyle} />
              </div>

              {/* Phone */}
              <div>
                <label style={labelStyle}>Phone Number</label>
                <input value={editPhone} onChange={(e) => setEditPhone(e.target.value)}
                  placeholder="+1 (555) 000-0000" type="tel" style={inputStyle} />
              </div>

              {/* Service type */}
              <div>
                <label style={labelStyle}>Service Type</label>
                <select value={editServiceType} onChange={(e) => setEditServiceType(e.target.value)}
                  style={{ ...inputStyle, cursor: "pointer" }}>
                  <option value="">— Not assigned —</option>
                  {SERVICE_TYPES.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
                </select>
              </div>

              {/* Deal value */}
              <div>
                <label style={labelStyle}>Deal Value (USD)</label>
                <input value={editDealValue} onChange={(e) => setEditDealValue(e.target.value)}
                  placeholder="e.g. 24000" style={inputStyle} />
              </div>

              {/* Next Follow-Up */}
              <div>
                <label style={labelStyle}>Next Follow-Up Date</label>
                <input type="date" value={editNextFollowUp} onChange={(e) => setEditNextFollowUp(e.target.value)}
                  style={{ ...inputStyle, cursor: "pointer" }} />
              </div>

              {/* Notes */}
              <div>
                <label style={labelStyle}>Notes</label>
                <textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Internal notes about this prospect…" rows={3}
                  style={{ ...inputStyle, resize: "vertical" }} />
              </div>

              <button onClick={handleSaveEdits} style={{
                padding: "11px", borderRadius: 10, background: B.purple, color: B.white,
                border: "none", fontSize: 14, fontWeight: 600, cursor: "pointer", marginTop: 4,
              }}>Save Changes</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── New Prospect Modal ───────────────────────────────────────────────────────
const NewProspectModal = ({ onClose, onAdd, industries = INDUSTRIES }) => {
  const [form, setForm] = useState({
    name: "", title: "", company: "", industry: industries[0] || "",
    email: "", phone: "", linkedin: "", priority: "medium",
    status: "identified", source: "Manual", serviceType: "", notes: "",
  });
  const [errors, setErrors] = useState({});
  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const handleSubmit = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = true;
    if (!form.company.trim()) errs.company = true;
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onAdd({
      ...form,
      name: form.name.trim(),
      company: form.company.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      linkedin: form.linkedin.trim() || null,
      dealValue: null,
    });
    onClose();
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(7,6,38,0.7)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(4px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ background: B.white, borderRadius: 18, width: "100%", maxWidth: 560, maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 64px rgba(7,6,38,0.3)" }}>
        <div style={{ padding: "24px 28px 16px", borderBottom: `1px solid ${B.gray200}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: B.black, fontFamily: "'Poppins', sans-serif" }}>Add New Prospect</div>
          <button onClick={onClose} style={{ background: B.gray100, border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={16} color={B.gray500} />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 28px", display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={labelStyle}>Full Name *</label>
              <input value={form.name} onChange={set("name")} placeholder="Jane Smith"
                style={{ ...inputStyle, borderColor: errors.name ? B.danger : B.gray200 }} />
            </div>
            <div>
              <label style={labelStyle}>Job Title</label>
              <input value={form.title} onChange={set("title")} placeholder="CIO" style={inputStyle} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={labelStyle}>Company *</label>
              <input value={form.company} onChange={set("company")} placeholder="Acme Corp"
                style={{ ...inputStyle, borderColor: errors.company ? B.danger : B.gray200 }} />
            </div>
            <div>
              <label style={labelStyle}>Industry</label>
              <select value={form.industry} onChange={set("industry")} style={{ ...inputStyle, cursor: "pointer" }}>
                {industries.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={labelStyle}>Email</label>
              <input value={form.email} onChange={set("email")} type="email" placeholder="jane@acme.com" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Phone</label>
              <input value={form.phone} onChange={set("phone")} type="tel" placeholder="+1 (555) 000-0000" style={inputStyle} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>LinkedIn URL</label>
            <input value={form.linkedin} onChange={set("linkedin")} placeholder="https://linkedin.com/in/..." style={inputStyle} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={labelStyle}>Priority</label>
              <select value={form.priority} onChange={set("priority")} style={{ ...inputStyle, cursor: "pointer" }}>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Pipeline Stage</label>
              <select value={form.status} onChange={set("status")} style={{ ...inputStyle, cursor: "pointer" }}>
                {PIPELINE_STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={labelStyle}>Service Type</label>
              <select value={form.serviceType} onChange={set("serviceType")} style={{ ...inputStyle, cursor: "pointer" }}>
                <option value="">— Not assigned —</option>
                {SERVICE_TYPES.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Source</label>
              <input value={form.source} onChange={set("source")} placeholder="LinkedIn, Referral…" style={inputStyle} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Notes</label>
            <textarea value={form.notes} onChange={set("notes")} rows={2} placeholder="Initial context…"
              style={{ ...inputStyle, resize: "vertical" }} />
          </div>
        </div>
        <div style={{ padding: "16px 28px", borderTop: `1px solid ${B.gray200}`, display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 10, background: B.gray100, color: B.gray600, border: "none", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSubmit} style={{ padding: "10px 20px", borderRadius: 10, background: B.purple, color: B.white, border: "none", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Add Prospect</button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function RevoyaDashboard({ user, onLogout }) {
  // Prospect data from Firestore (real-time, shared across all devices)
  const { prospects: prospectData, loading: prospectsLoading, addProspect, updateProspect } = useProspects();

  const isMobile = useIsMobile(480);
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem("revoya_active_tab") || "overview");
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterIndustry, setFilterIndustry] = useState("all");
  const [filterFollowUp, setFilterFollowUp] = useState(false);
  const [filterNurture, setFilterNurture] = useState(false);
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeModal, setActiveModal] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [newProspectOpen, setNewProspectOpen] = useState(false);
  const [importExportOpen, setImportExportOpen] = useState(false);
  const [importToast, setImportToast] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeProspect, setComposeProspect] = useState(null);
  const [composeContext, setComposeContext] = useState(null); // { touch, label, channel, templateId }
  const [logTouchTarget, setLogTouchTarget] = useState(null);
  const [templates, setTemplates] = useState(() => {
    try { return JSON.parse(localStorage.getItem("revoya_templates")) || DEFAULT_TEMPLATES; }
    catch { return DEFAULT_TEMPLATES; }
  });

  useEffect(() => {
    if (importToast) { const t = setTimeout(() => setImportToast(null), 5000); return () => clearTimeout(t); }
  }, [importToast]);

  useEffect(() => { setCurrentPage(1); }, [search, filterStatus, filterIndustry, pageSize]);

  const handleTabChange = (id) => { setActiveTab(id); localStorage.setItem("revoya_active_tab", id); setMobileNavOpen(false); };

  const { addActivity, deleteActivity, getActivities, activities } = useActivities();
  const { members: teamMembers, addMember, removeMember } = useTeamMembers();

  const fullName = user?.name || "Sales Team";
  const firstName = fullName.split(" ")[0];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  // ── Normalize statuses from raw Firestore data (handles CRM import variants) ─
  const normalizedData = prospectData.map((p) => ({ ...p, status: normalizeStatus(p.status) }));

  // ── Derived stats ─────────────────────────────────────────────────────────
  const contacted = normalizedData.filter((p) => p.status === "contacted");
  const inPipeline = normalizedData.filter((p) => ["contacted", "discovery", "proposal"].includes(p.status)).length;
  const emailNeeded = normalizedData.filter((p) => !p.email);
  const projectProspects = normalizedData.filter((p) => ["proposal", "closed"].includes(p.status));
  const totalRevenue = projectProspects.reduce((s, p) => s + (p.dealValue || 0), 0);
  const reachedOut = normalizedData.filter(p => ["contacted","discovery","proposal","closed"].includes(p.status)).length;
  const discoveryCount = normalizedData.filter(p => ["discovery","proposal","closed"].includes(p.status)).length;
  const discoveryRate = reachedOut > 0 ? Math.round(discoveryCount / reachedOut * 100) : 0;

  const liveIndustries = [...new Set(normalizedData.map((p) => p.industry).filter(Boolean))].sort();
  const industryBreakdown = liveIndustries.map((ind) => ({
    name: ind.replace(" Services", "").replace(" & Professional", ""),
    count: normalizedData.filter((p) => p.industry === ind).length,
  })).sort((a, b) => b.count - a.count);


  // ── Filtered prospects for outreach ──────────────────────────────────────
  const filtered = normalizedData.filter((p) => {
    const q = search.toLowerCase();
    return (
      (!q || p.name.toLowerCase().includes(q) || p.company.toLowerCase().includes(q) || p.title.toLowerCase().includes(q)) &&
      (filterStatus === "all" || p.status === filterStatus) &&
      (filterIndustry === "all" || p.industry === filterIndustry) &&
      (!filterFollowUp || isFollowUpDue(p)) &&
      (!filterNurture || p.nurture)
    );
  }).sort((a, b) => filterFollowUp ? (getDaysOverdue(b) || 0) - (getDaysOverdue(a) || 0) : 0);

  // ── Recent activity feed ──────────────────────────────────────────────────
  const recentActivities = Object.entries(activities)
    .flatMap(([pid, acts]) => acts.map((a) => ({ ...a, prospect: prospectData.find((p) => p.id === Number(pid)) })))
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 8);

  // action: { type: "compose"|"prospect"|"tab", prospectId?, tab? }
  const addNotification = useCallback((message, type = "success", action = null) => {
    setNotifications(prev => [{ id: Date.now(), message, type, time: new Date(), read: false, action }, ...prev]);
  }, []);

  const handleMarkTouchSent = useCallback(async (prospect, touch) => {
    const today = new Date().toISOString();
    const seq = getSequence(prospect).map(t => t.touch === touch.touch ? { ...t, sent: true, sentDate: today } : { ...t });
    const nextTouchNum = touch.touch + 1;
    const isLast = touch.touch === 5;
    const updates = isLast
      ? { touchSequence: seq, touchNumber: 6, nurture: true, nurtureReactivateDate: (() => { const d = new Date(); d.setDate(d.getDate() + 90); return d.toISOString(); })(), lastTouchSentAt: today, lastContact: today, nextFollowUp: null }
      : { touchSequence: seq, touchNumber: nextTouchNum, lastTouchSentAt: today, lastContact: today,
          nextFollowUp: (() => { const base = new Date(touch.touch === 1 ? today : (seq.find(t => t.touch === 1)?.sentDate || today)); const next = DEFAULT_TOUCH_SEQUENCE.find(t => t.touch === nextTouchNum); base.setDate(base.getDate() + (next?.dayOffset || 0)); return base.toISOString(); })(),
          ...(prospect.status === "identified" ? { status: "contacted" } : {}),
        };
    await updateProspect(prospect.id, updates);
    if (isLast) {
      addNotification(`${prospect.name} moved to Nurture — reactivates in 90 days`, "success");
    } else {
      addNotification(`T${touch.touch} logged for ${prospect.name} · next due in ${DEFAULT_TOUCH_SEQUENCE.find(t => t.touch === nextTouchNum)?.dayOffset - touch.dayOffset || 0}d`, "success");
    }
    setLogTouchTarget(null);
  }, [updateProspect, addNotification]);

  const handleBulkStatusUpdate = useCallback((newStatus) => {
    const count = selectedIds.size;
    selectedIds.forEach(id => updateProspect(id, { status: newStatus }));
    setSelectedIds(new Set());
    addNotification(`Moved ${count} prospect${count !== 1 ? "s" : ""} to ${PIPELINE_STAGES.find(s => s.key === newStatus)?.label}`, "success");
  }, [selectedIds, updateProspect, addNotification]);

  const remindersSeeded = useRef(false);
  useEffect(() => {
    if (remindersSeeded.current || !prospectData.length) return;
    remindersSeeded.current = true;
    const due = normalizedData.filter(p => isFollowUpDue(p));
    due.slice(0, 5).forEach(p => {
      const touch = getCurrentTouch(p);
      addNotification(
        `${touch?.label || "Follow-up"} due: ${p.name} · ${p.company}`,
        "warning",
        touch?.channel === "email" && p.email ? { type: "compose", prospectId: p.id } : { type: "prospect", prospectId: p.id }
      );
    });
  }, [prospectData.length]); // eslint-disable-line

  const importCSV = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const lines = e.target.result.split("\n").map(l => l.trim()).filter(Boolean);
      if (lines.length < 2) {
        addNotification("Import failed: file has no data rows.", "error");
        setImportToast({ message: "Import failed — file has no data rows.", type: "error" });
        return;
      }
      const headers = parseCSVLine(lines[0]).map(h => h.replace(/"/g, "").toLowerCase().trim());
      const sample = lines.slice(1, 8).map(l => parseCSVLine(l).map(c => c.replace(/^"|"$/g, "").trim()));

      // Content sniff helpers
      const isEmail = v => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);
      const isPhone = v => /^[\d\s+\-().]{7,20}$/.test(v) && v.replace(/\D/g, "").length >= 7;
      const isLinkedIn = v => v.includes("linkedin.com");

      const dc = (kws, test) => detectCol(headers, sample, kws, test);

      // Name: detect first+last columns separately, or a combined name column
      const firstIdx = dc(["first name", "firstname", "first_name"], null);
      const lastIdx = dc(["last name", "lastname", "last_name", "surname"], null);
      // Only look for a combined "name" column if first+last aren't both found
      const nameIdx = (firstIdx >= 0 && lastIdx >= 0) ? -1
        : dc(["name", "full name", "contact name", "full_name", "contact"], null);

      const companyIdx = dc(["company", "company name", "organization", "firm", "account name", "account"], null);
      const titleIdx   = dc(["title", "job title", "position", "role", "job_title"], null);
      const industryIdx= dc(["industry", "sector", "vertical"], null);
      const emailIdx   = dc(["email", "email address", "work email", "e-mail", "email_address"], isEmail);
      const phoneIdx   = dc(["phone", "phone number", "mobile", "mobile phone", "work phone", "tel", "telephone"], isPhone);
      const linkedinIdx= dc(["linkedin", "linkedin url", "linkedin profile", "linkedin_url"], isLinkedIn);
      const priorityIdx= dc(["priority"], null);
      const statusIdx  = dc(["status", "stage", "pipeline stage", "lead status", "lifecycle stage"], null);
      const dealValueIdx=dc(["deal value", "deal size", "deal amount", "revenue", "amount", "value"], null);

      const existingEmails = new Set(prospectData.map(p => p.email?.toLowerCase()).filter(Boolean));
      const existingKeys = new Set(prospectData.map(p => `${p.name.toLowerCase()}|${p.company.toLowerCase()}`));
      let added = 0, dupes = 0, invalid = 0;

      lines.slice(1).forEach((line) => {
        const cells = parseCSVLine(line).map(c => c.replace(/^"|"$/g, "").trim());
        // Resolve name from combined column or first+last
        let name = nameIdx >= 0 ? cells[nameIdx] || "" : "";
        if (!name && (firstIdx >= 0 || lastIdx >= 0)) {
          const first = firstIdx >= 0 ? cells[firstIdx] || "" : "";
          const last  = lastIdx  >= 0 ? cells[lastIdx]  || "" : "";
          name = [first, last].filter(Boolean).join(" ");
        }
        const company = companyIdx >= 0 ? cells[companyIdx] || "" : "";
        if (!name || !company) { invalid++; return; }
        const email = emailIdx >= 0 && cells[emailIdx] ? cells[emailIdx] : null;
        const emailKey = email?.toLowerCase();
        const nameKey = `${name.toLowerCase()}|${company.toLowerCase()}`;
        if ((emailKey && existingEmails.has(emailKey)) || existingKeys.has(nameKey)) { dupes++; return; }
        addProspect({
          name, company,
          title:    titleIdx   >= 0 ? cells[titleIdx]    || "" : "",
          industry: industryIdx >= 0 && cells[industryIdx] ? cells[industryIdx] : "",
          email,
          phone:    phoneIdx   >= 0 && cells[phoneIdx]   ? cells[phoneIdx]   : null,
          linkedin: linkedinIdx >= 0 && cells[linkedinIdx]? cells[linkedinIdx]: null,
          priority: priorityIdx >= 0 && cells[priorityIdx] ? cells[priorityIdx].toLowerCase() : "medium",
          status:   statusIdx  >= 0 && cells[statusIdx]  ? normalizeStatus(cells[statusIdx]) : "identified",
          dealValue: dealValueIdx >= 0 ? (parseFloat((cells[dealValueIdx] || "").replace(/[^0-9.]/g, "")) || null) : null,
          notes: "",
        });
        if (emailKey) existingEmails.add(emailKey);
        existingKeys.add(nameKey);
        added++;
      });

      const parts = [];
      if (added   > 0) parts.push(`${added}   prospect${added   !== 1 ? "s" : ""} added`);
      if (dupes   > 0) parts.push(`${dupes}   duplicate${dupes  !== 1 ? "s" : ""} skipped`);
      if (invalid > 0) parts.push(`${invalid} row${invalid !== 1 ? "s" : ""} missing name/company`);
      const msg  = `Import complete — ${parts.join(", ") || "nothing changed"}.`;
      const type = added > 0 ? "success" : "warning";
      addNotification(msg, type);
      setImportToast({ message: msg, type });
    };
    reader.readAsText(file);
  }, [addProspect, prospectData, addNotification]);

  const downloadTemplate = useCallback(() => {
    const headers = ["Name","Company","Title","Industry","Email","Phone","LinkedIn","Priority","Status","Deal Value"];
    const example = ["Jane Smith","Acme Corp","VP of Sales","Technology","jane@acme.com","+1 555-0100","https://linkedin.com/in/janesmith","high","identified","25000"];
    const csv = [headers.join(","), example.map(v => `"${v}"`).join(",")].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "revoya_import_template.csv"; a.click();
    URL.revokeObjectURL(url);
  }, []);

  const exportCSV = useCallback((data, filename = "revoya_prospects.csv") => {
    const src = data || prospectData;
    const headers = ["Name","Company","Title","Industry","Email","Phone","LinkedIn","Priority","Status","Deal Value","Notes"];
    const rows = src.map(p => [
      p.name, p.company, p.title || "", p.industry || "", p.email || "",
      p.phone || "", p.linkedin || "", p.priority || "", p.status || "",
      p.dealValue || "", (p.notes || "").replace(/\n/g, " "),
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  }, [prospectData]);

  // ─── Import/Export Modal ───────────────────────────────────────────────────
  const ImportExportModal = () => {
    const [dragOver, setDragOver] = useState(false);
    const [importTab, setImportTab] = useState("import"); // "import" | "export"
    const fileInputRef = useRef();

    const handleFile = (file) => {
      if (!file) return;
      setImportExportOpen(false);
      setTimeout(() => importCSV(file), 100);
    };

    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(7,6,38,0.55)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
        onClick={() => setImportExportOpen(false)}>
        <div style={{ background: B.white, borderRadius: 20, width: "100%", maxWidth: 680, boxShadow: "0 24px 64px rgba(7,6,38,0.25)", overflow: "hidden" }}
          onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div style={{ padding: "24px 28px 0", borderBottom: `1px solid ${B.gray200}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: B.black, fontFamily: "'Poppins', sans-serif" }}>Import / Export</div>
              <button onClick={() => setImportExportOpen(false)} style={{ background: B.gray100, border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={15} color={B.gray500} />
              </button>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              {["import","export"].map(t => (
                <button key={t} onClick={() => setImportTab(t)} style={{ padding: "8px 20px", borderRadius: "8px 8px 0 0", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, background: importTab === t ? B.white : "transparent", color: importTab === t ? B.purple : B.gray500, borderBottom: importTab === t ? `2px solid ${B.purple}` : "2px solid transparent", textTransform: "capitalize", transition: "all 0.15s" }}>
                  {t === "import" ? "Import Prospects" : "Export Data"}
                </button>
              ))}
            </div>
          </div>

          <div style={{ padding: "24px 28px" }}>
            {importTab === "import" ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
                  onClick={() => fileInputRef.current?.click()}
                  style={{ border: `2px dashed ${dragOver ? B.purple : B.gray300}`, borderRadius: 14, padding: "48px 20px", textAlign: "center", cursor: "pointer", background: dragOver ? B.purplePale : B.gray50, transition: "all 0.15s" }}>
                  <FileText size={28} color={dragOver ? B.purple : B.gray400} style={{ marginBottom: 10 }} />
                  <div style={{ fontSize: 13, fontWeight: 600, color: dragOver ? B.purple : B.gray600, marginBottom: 4 }}>
                    {dragOver ? "Drop to import" : "Drop your CSV here"}
                  </div>
                  <div style={{ fontSize: 12, color: B.gray400 }}>or click to browse</div>
                  <input ref={fileInputRef} type="file" accept=".csv" style={{ display: "none" }} onChange={e => { handleFile(e.target.files[0]); e.target.value = ""; }} />
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <button onClick={downloadTemplate} style={{ padding: "9px 16px", background: B.purplePale, color: B.purple, border: `1px solid ${B.purple}20`, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                    <FileText size={13} /> Download Template
                  </button>
                  <div style={{ fontSize: 11, color: B.gray400 }}>Not sure of the format? Download the template first.</div>
                </div>
                <div style={{ padding: "9px 14px", background: B.warningBg, borderRadius: 8, fontSize: 11, color: B.warning, fontWeight: 500 }}>
                  Excel files (.xlsx) must be saved as CSV first: File → Save As → CSV.
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button onClick={() => { exportCSV(prospectData, "revoya_all_prospects.csv"); setImportExportOpen(false); }}
                  style={{ padding: "12px 18px", background: B.purple, color: B.white, border: "none", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                  <FileText size={14} /> Export All Prospects ({prospectData.length})
                </button>
                <button onClick={() => { exportCSV(filtered, "revoya_filtered_prospects.csv"); setImportExportOpen(false); }}
                  style={{ padding: "12px 18px", background: B.white, color: B.gray600, border: `1px solid ${B.gray200}`, borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                  <FileText size={14} /> Export Current View ({filtered.length})
                </button>
                <button onClick={() => { exportCSV(normalizedData.filter(p => p.status === "closed"), "revoya_clients.csv"); setImportExportOpen(false); }}
                  style={{ padding: "12px 18px", background: B.white, color: B.gray600, border: `1px solid ${B.gray200}`, borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                  <FileText size={14} /> Export Clients Only ({normalizedData.filter(p => p.status === "closed").length})
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const navItems = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "pipeline", label: "Sales Pipeline", icon: TrendingUp },
    { id: "clients", label: "Client Management", icon: Users },
    { id: "projects", label: "Projects", icon: FolderKanban },
    { id: "outreach", label: "Outreach", icon: Send },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  const sidebarW = isMobile ? 0 : (collapsed ? 68 : 220);

  const pad = isMobile ? "20px 16px" : "32px 36px";

  // ─── Team Members Section (Settings) ─────────────────────────────────────
  const TeamMembersSection = ({ user: u, teamMembers: members, addMember, removeMember }) => {
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", role: "" });

    const save = async () => {
      if (!form.name.trim() || !form.email.trim()) return;
      await addMember({ name: form.name.trim(), email: form.email.trim(), role: form.role.trim() });
      setForm({ name: "", email: "", role: "" });
      setAdding(false);
    };

    return (
      <div style={{ background: B.white, borderRadius: 16, padding: "24px 28px", border: `1px solid ${B.gray200}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: B.black, fontFamily: "'Poppins', sans-serif" }}>Team Members</div>
          <button onClick={() => setAdding(a => !a)} style={{ padding: "6px 14px", borderRadius: 8, background: B.purple, color: B.white, border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            {adding ? "Cancel" : "+ Add Member"}
          </button>
        </div>
        {adding && (
          <div style={{ background: B.gray50, borderRadius: 10, padding: "16px", border: `1px solid ${B.gray200}`, marginBottom: 16, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <input placeholder="Full name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                style={{ flex: 1, minWidth: 140, padding: "8px 12px", borderRadius: 8, border: `1px solid ${B.gray200}`, fontSize: 13, fontFamily: "inherit", outline: "none" }} />
              <input placeholder="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                style={{ flex: 1, minWidth: 180, padding: "8px 12px", borderRadius: 8, border: `1px solid ${B.gray200}`, fontSize: 13, fontFamily: "inherit", outline: "none" }} />
              <input placeholder="Role (e.g. Head of Sales)" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                style={{ flex: 1, minWidth: 160, padding: "8px 12px", borderRadius: 8, border: `1px solid ${B.gray200}`, fontSize: 13, fontFamily: "inherit", outline: "none" }} />
            </div>
            <button onClick={save} style={{ alignSelf: "flex-end", padding: "8px 20px", borderRadius: 8, background: B.purple, color: B.white, border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Save</button>
          </div>
        )}
        {members.map((m, i) => (
          <div key={m.id || m.email} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 0", borderBottom: i < members.length - 1 ? `1px solid ${B.gray100}` : "none" }}>
            <div style={{ width: 38, height: 38, borderRadius: "50%", background: B.purplePale, display: "flex", alignItems: "center", justifyContent: "center", color: B.purple, fontSize: 14, fontWeight: 700, flexShrink: 0 }}>{m.name.charAt(0)}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: B.black }}>{m.name}</div>
              <div style={{ fontSize: 12, color: B.gray500 }}>{m.role && <span style={{ marginRight: 6 }}>{m.role} ·</span>}{m.email}</div>
            </div>
            {m.email === u?.email
              ? <span style={{ fontSize: 11, fontWeight: 700, background: B.purplePale, color: B.purple, borderRadius: 6, padding: "2px 10px" }}>You</span>
              : m.id && <button onClick={() => removeMember(m.id)} style={{ background: "none", border: "none", color: B.gray400, fontSize: 12, cursor: "pointer", padding: "4px 8px", borderRadius: 6 }} onMouseEnter={e => e.currentTarget.style.color = B.danger} onMouseLeave={e => e.currentTarget.style.color = B.gray400}>Remove</button>
            }
          </div>
        ))}
        {members.length === 0 && <div style={{ fontSize: 13, color: B.gray400 }}>No team members yet. Add your first one above.</div>}
      </div>
    );
  };

  // ─── Template Composer Modal ─────────────────────────────────────────────
  const TemplateComposerModal = () => {
    const p = composeProspect;
    const touchContextId = composeContext?.templateId || (composeContext?.touch ? DEFAULT_TEMPLATES.find(t => t.touchNumber === composeContext.touch)?.id : null);
    const [selId, setSelId] = useState(touchContextId || DEFAULT_TEMPLATES[0].id);
    const [customBody, setCustomBody] = useState(null);
    const [researchHook, setResearchHook] = useState("");

    const isDefaultStage = DEFAULT_TEMPLATES.some(d => d.id === selId);
    const titleGroup = getTitleGroup(p.title);

    // Sender signature strings
    const myMember = teamMembers.find(m => m.email?.toLowerCase() === user?.email?.toLowerCase());
    const senderRole = myMember?.role || "";
    const fullSig = [
      fullName,
      senderRole ? `${senderRole}, Revoya` : "Revoya",
      "AI Governance Advisory for Enterprise Teams",
      `${user?.email} · revoya.ai`,
    ].join("\n");
    const shortSig = `${fullName}\nRevoya · ${user?.email}`;

    const fillVars = (text) => (text || "")
      .replace(/\{\{firstName\}\}/g, (p.name || "").split(" ")[0])
      .replace(/\{\{company\}\}/g, p.company || "")
      .replace(/\{\{industry\}\}/g, p.industry || "your industry")
      .replace(/\{\{sig\}\}/g, fullSig)
      .replace(/\{\{shortSig\}\}/g, shortSig);

    const computeSubject = () => {
      const tpl = isDefaultStage
        ? (STAGE_SUBJECTS[selId] || "")
        : (templates.find(t => t.id === selId)?.subject || "");
      return fillVars(tpl);
    };

    const computeBody = () => {
      if (!isDefaultStage) {
        const tpl = templates.find(t => t.id === selId);
        return fillVars(tpl?.body || "");
      }
      const raw = GROUP_BODIES[titleGroup]?.[selId] || GROUP_BODIES.B[selId] || "";
      return fillVars(raw).replace(/\[RESEARCH HOOK\]/g, researchHook.trim() || "[RESEARCH HOOK]");
    };

    const subject = computeSubject();
    const body = customBody !== null ? customBody : computeBody();

    useEffect(() => { setCustomBody(null); setResearchHook(""); }, [selId]); // eslint-disable-line

    const openEmail = () => {
      const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(p.email)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.open(gmailUrl, "_blank");
      if (composeContext) {
        handleMarkTouchSent(p, composeContext);
        setComposeContext(null);
      } else {
        updateProspect(p.id, { lastContact: new Date().toISOString() });
      }
      addNotification(`Gmail compose opened for ${p.name}`, "success");
    };

    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(7,6,38,0.7)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(4px)" }}
        onClick={e => { if (e.target === e.currentTarget) { setComposeOpen(false); setComposeContext(null); } }}>
        <div style={{ background: B.white, borderRadius: 20, width: "min(700px, 95vw)", maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 64px rgba(7,6,38,0.3)", overflow: "hidden" }}>
          {/* Header */}
          <div style={{ padding: "18px 24px", borderBottom: `1px solid ${B.gray200}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: B.black }}>Compose Outreach</span>
                {composeContext && <TouchBadge prospect={{ touchNumber: composeContext.touch, nurture: false, touchSequence: getSequence(p) }} />}
              </div>
              <div style={{ fontSize: 12, color: B.gray500, marginTop: 2 }}>
                {p.name} · {p.title || p.company}
                {composeContext && <span style={{ marginLeft: 8, color: B.purple, fontWeight: 600 }}>{composeContext.label} via {composeContext.channel}</span>}
              </div>
            </div>
            <button onClick={() => { setComposeOpen(false); setComposeContext(null); }} style={{ background: B.gray100, border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><X size={15} color={B.gray500} /></button>
          </div>
          {/* Prospect context */}
          <div style={{ padding: "12px 24px", background: B.gray50, borderBottom: `1px solid ${B.gray100}`, display: "flex", gap: 24, flexWrap: "wrap" }}>
            {[["NAME", p.name], ["TITLE", p.title || "—"], ["COMPANY", p.company], ["INDUSTRY", p.industry || "—"]].map(([label, val]) => (
              <div key={label}>
                <div style={{ fontSize: 10, fontWeight: 700, color: B.gray400, letterSpacing: 0.5 }}>{label}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: B.black, marginTop: 2 }}>{val}</div>
              </div>
            ))}
            {isDefaultStage && (
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: B.gray400, marginRight: 6 }}>GROUP</span>
                <span style={{ fontSize: 11, fontWeight: 700, background: B.purplePale, color: B.purple, borderRadius: 5, padding: "2px 8px" }}>
                  {titleGroup === "A" ? "CISO" : titleGroup === "C" ? "GRC / Risk" : "Compliance"}
                </span>
              </div>
            )}
            {p.linkedin && <a href={p.linkedin} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#0A66C2", textDecoration: "none", fontWeight: 600 }}><Linkedin size={13} /> LinkedIn</a>}
          </div>
          {/* Template picker */}
          <div style={{ padding: "12px 24px", borderBottom: `1px solid ${B.gray100}`, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: B.gray400, marginRight: 4 }}>STAGE</span>
            {templates.map(t => (
              <button key={t.id} onClick={() => setSelId(t.id)}
                style={{ padding: "5px 13px", borderRadius: 20, border: `1.5px solid ${selId === t.id ? B.purple : B.gray200}`, background: selId === t.id ? B.purplePale : B.white, color: selId === t.id ? B.purple : B.gray600, fontSize: 12, fontWeight: selId === t.id ? 700 : 500, cursor: "pointer" }}>
                {t.name}
              </button>
            ))}
          </div>
          {/* Research hook — Initial Outreach only */}
          {selId === "initial" && isDefaultStage && (
            <div style={{ padding: "14px 24px", borderBottom: `1px solid ${B.gray100}`, background: "#FFFBF0" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: B.gray400, letterSpacing: 0.5, marginBottom: 6 }}>
                RESEARCH HOOK <span style={{ color: B.danger, fontWeight: 700 }}>*</span>
              </div>
              <input
                value={researchHook}
                onChange={e => { setResearchHook(e.target.value); setCustomBody(null); }}
                placeholder="One sentence about a recent AI initiative, product launch, hiring signal, or funding round at this company."
                style={{ width: "100%", padding: "9px 14px", borderRadius: 8, border: `1px solid ${researchHook.trim() ? B.gray200 : B.warning}`, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box", background: B.white }}
              />
            </div>
          )}
          {/* Preview */}
          <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: B.gray400, letterSpacing: 0.5, marginBottom: 6 }}>SUBJECT</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: B.black, background: B.gray50, borderRadius: 8, padding: "10px 14px", border: `1px solid ${B.gray200}` }}>{subject}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: B.gray400, letterSpacing: 0.5, marginBottom: 6 }}>BODY <span style={{ fontSize: 10, fontWeight: 500, color: B.gray400 }}>(editable)</span></div>
              <textarea value={body} onChange={e => setCustomBody(e.target.value)}
                style={{ width: "100%", minHeight: 240, fontSize: 13, color: B.gray600, background: B.gray50, borderRadius: 8, padding: "14px 16px", border: `1px solid ${B.gray200}`, fontFamily: "inherit", lineHeight: 1.7, resize: "vertical", outline: "none", boxSizing: "border-box" }} />
            </div>
          </div>
          {/* Footer */}
          <div style={{ padding: "14px 24px", borderTop: `1px solid ${B.gray200}`, display: "flex", gap: 10, justifyContent: "flex-end", alignItems: "center" }}>
            {customBody !== null && <button onClick={() => setCustomBody(null)} style={{ padding: "9px 16px", borderRadius: 9, border: `1px solid ${B.gray200}`, background: B.white, color: B.gray500, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Reset</button>}
            <button onClick={() => setComposeOpen(false)} style={{ padding: "9px 18px", borderRadius: 9, border: `1px solid ${B.gray200}`, background: B.white, color: B.gray600, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
            <button onClick={openEmail} disabled={!p.email} style={{ padding: "9px 22px", borderRadius: 9, background: p.email ? B.purple : B.gray300, color: B.white, border: "none", fontSize: 13, fontWeight: 700, cursor: p.email ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: 6 }}>
              <Mail size={14} /> Open in Gmail
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Log Touch Confirm Modal ──────────────────────────────────────────────
  const LogTouchModal = () => {
    const p = logTouchTarget;
    if (!p) return null;
    const touch = getCurrentTouch(p);
    const isLinkedIn = touch?.channel === "linkedin";
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(7,6,38,0.65)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
        onClick={e => e.target === e.currentTarget && setLogTouchTarget(null)}>
        <div style={{ background: B.white, borderRadius: 16, width: "min(440px, 95vw)", padding: "28px", boxShadow: "0 16px 48px rgba(7,6,38,0.2)" }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: B.black, marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
            Log Touch {touch && <TouchBadge prospect={p} />}
          </div>
          {isLinkedIn ? (
            <div style={{ fontSize: 13, color: B.gray600, marginBottom: 20, lineHeight: 1.6 }}>
              <strong>Touch 3 — LinkedIn Value Touch</strong><br />
              Connect on LinkedIn or engage with a recent post from <strong>{p.name}</strong> at {p.company}. No direct pitch — just visibility.<br /><br />
              {p.linkedin && <a href={p.linkedin} target="_blank" rel="noreferrer" style={{ color: "#0A66C2", fontWeight: 600 }}>Open LinkedIn profile →</a>}
              <br /><br />Mark this touch as done once you've engaged?
            </div>
          ) : (
            <div style={{ fontSize: 13, color: B.gray600, marginBottom: 20, lineHeight: 1.6 }}>
              Mark <strong>{p.name}</strong> ({p.company}) as having received <strong>{touch?.label || "this touch"}</strong>
              {touch?.touch === 5 && <span style={{ display: "block", marginTop: 8, color: B.warning, fontWeight: 600 }}>This is the final touch. They will be moved to Nurture for 90 days.</span>}
            </div>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={() => setLogTouchTarget(null)} style={{ padding: "9px 18px", borderRadius: 8, background: B.gray100, border: "none", color: B.gray600, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>No, Cancel</button>
            <button onClick={() => handleMarkTouchSent(p, touch)} style={{ padding: "9px 20px", borderRadius: 8, background: B.purple, border: "none", color: B.white, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Yes, Mark Sent</button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Overview Tab ────────────────────────────────────────────────────────
  const OverviewTab = () => (
    <div style={{ padding: pad, display: "flex", flexDirection: "column", gap: isMobile ? 16 : 28 }}>
      {/* Banner */}
      <div style={{ background: `linear-gradient(135deg, ${B.purpleDark} 0%, ${B.purple} 60%, #9B8CF8 100%)`, borderRadius: 16, padding: isMobile ? "20px 20px" : "32px 36px", color: B.white, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.7, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Dashboard Overview</div>
          <div style={{ fontSize: isMobile ? 20 : 28, fontWeight: 700, fontFamily: "'Poppins', sans-serif", marginBottom: 6 }}>{greeting}, {firstName} 👋</div>
          <div style={{ fontSize: 13, opacity: 0.85 }}>
            <strong>{contacted.length}</strong> contacted · <strong>{inPipeline}</strong> in pipeline
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ background: "rgba(255,255,255,0.15)", borderRadius: 12, padding: "10px 16px", textAlign: "center" }}>
            <div style={{ fontSize: isMobile ? 24 : 32, fontWeight: 800, fontFamily: "'Poppins', sans-serif" }}>{prospectData.length}</div>
            <div style={{ fontSize: 11, opacity: 0.8 }}>Prospects</div>
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: isMobile ? 10 : 16 }}>
        <MetricCard icon={Target} label="In Pipeline" value={inPipeline} change={inPipeline > 0 ? "actively engaged" : "start outreach"} changeType="up" lime />
        <MetricCard icon={Send} label="Contacted" value={contacted.length} />
        <MetricCard icon={Mail} label="Email Needed" value={emailNeeded.length} change="find emails" changeType="down" />
        <MetricCard icon={Activity} label="Discovery Rate" value={`${discoveryRate}%`} change={reachedOut > 0 ? `${discoveryCount} of ${reachedOut} contacted` : "contacted → discovery"} changeType={discoveryRate > 0 ? "up" : "neutral"} />
        {totalRevenue > 0 && <MetricCard icon={DollarSign} label="Pipeline Revenue" value={`$${totalRevenue.toLocaleString()}`} changeType="up" />}
      </div>

      {/* Follow-up Summary Widget */}
      {(() => {
        const due = normalizedData.filter(p => isFollowUpDue(p));
        const overdue = due.filter(p => getDaysOverdue(p) > 0);
        const emailDue = due.filter(p => getCurrentTouch(p)?.channel === "email").length;
        const linkedInDue = due.filter(p => getCurrentTouch(p)?.channel === "linkedin").length;
        const urgency = overdue.length >= 6 ? B.danger : overdue.length >= 1 ? B.warning : B.success;
        const nurturing = normalizedData.filter(p => p.nurture);
        const reactivatingThisWeek = nurturing.filter(p => {
          if (!p.nurtureReactivateDate) return false;
          const d = new Date(p.nurtureReactivateDate);
          const now = new Date();
          const week = new Date(); week.setDate(week.getDate() + 7);
          return d >= now && d <= week;
        }).length;
        return (
          <>
            {due.length > 0 && (
              <div style={{ background: B.white, borderRadius: 14, padding: "18px 22px", border: `2px solid ${urgency}44`, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: `${urgency}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Clock size={20} color={urgency} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 26, fontWeight: 800, color: urgency, fontFamily: "'Poppins', sans-serif", lineHeight: 1 }}>{due.length}</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: B.black }}>follow-up{due.length !== 1 ? "s" : ""} due</span>
                    {overdue.length > 0 && <span style={{ fontSize: 11, fontWeight: 700, background: B.dangerBg, color: B.danger, borderRadius: 6, padding: "2px 8px" }}>{overdue.length} overdue</span>}
                  </div>
                  <div style={{ fontSize: 12, color: B.gray500, marginTop: 3 }}>
                    {emailDue > 0 && <span style={{ marginRight: 12 }}>✉ {emailDue} email{emailDue !== 1 ? "s" : ""}</span>}
                    {linkedInDue > 0 && <span>LinkedIn {linkedInDue}</span>}
                  </div>
                </div>
                <button onClick={() => { setFilterFollowUp(true); handleTabChange("outreach"); }}
                  style={{ padding: "9px 20px", borderRadius: 9, background: urgency, color: B.white, border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
                  Review Follow-ups
                </button>
              </div>
            )}
            {nurturing.length > 0 && (
              <div style={{ background: B.gray50, borderRadius: 12, padding: "14px 20px", border: `1px solid ${B.gray200}`, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: B.gray600 }}>Nurture · {nurturing.length} prospect{nurturing.length !== 1 ? "s" : ""}</span>
                  {reactivatingThisWeek > 0 && <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 700, background: B.purplePale, color: B.purple, borderRadius: 6, padding: "2px 8px" }}>{reactivatingThisWeek} re-activating this week</span>}
                </div>
                <button onClick={() => { setFilterNurture(true); setFilterFollowUp(false); handleTabChange("outreach"); }}
                  style={{ fontSize: 12, fontWeight: 600, color: B.gray500, background: "none", border: `1px solid ${B.gray200}`, borderRadius: 7, padding: "5px 12px", cursor: "pointer" }}>
                  View Nurture
                </button>
              </div>
            )}
          </>
        );
      })()}

      {/* Charts */}
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 12 : 20 }}>
        <div style={{ flex: 1.4, background: B.white, borderRadius: 16, padding: "24px 28px", border: `1px solid ${B.gray200}` }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: B.black, marginBottom: 20, fontFamily: "'Poppins', sans-serif" }}>Prospects by Industry</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={industryBreakdown} barSize={26}>
              <CartesianGrid strokeDasharray="3 3" stroke={B.gray100} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: B.gray500 }} />
              <YAxis tick={{ fontSize: 11, fill: B.gray500 }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${B.gray200}`, fontSize: 12 }} />
              <Bar dataKey="count" fill={B.purple} radius={[5, 5, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ flex: 0.8, background: B.white, borderRadius: 16, padding: "24px 28px", border: `1px solid ${B.gray200}` }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: B.black, marginBottom: 4, fontFamily: "'Poppins', sans-serif" }}>Contact Rate</div>
          <div style={{ fontSize: 11, color: B.gray400, marginBottom: 16 }}>Prospects reached vs pending</div>
          <div style={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <ResponsiveContainer width="100%" height={160}>
              <RechartsPie>
                <Pie data={[{ name: "Reached", value: contacted.length, color: B.purple }, { name: "Pending", value: Math.max(prospectData.length - contacted.length, 0), color: B.gray200 }]}
                  cx="50%" cy="50%" innerRadius={45} outerRadius={72} dataKey="value" paddingAngle={2} startAngle={90} endAngle={-270}>
                  {[B.purple, B.gray200].map((c, i) => <Cell key={i} fill={c} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${B.gray200}`, fontSize: 12 }} />
              </RechartsPie>
            </ResponsiveContainer>
            <div style={{ position: "absolute", textAlign: "center", pointerEvents: "none" }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: B.black, fontFamily: "'Poppins', sans-serif" }}>
                {prospectData.length ? Math.round((contacted.length / prospectData.length) * 100) : 0}%
              </div>
              <div style={{ fontSize: 10, color: B.gray400 }}>reached</div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
            {[{ name: "Reached", value: contacted.length, color: B.purple }, { name: "Pending", value: prospectData.length - contacted.length, color: B.gray300 }].map(d => (
              <div key={d.name} style={{ display: "flex", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: d.color }} />
                  <span style={{ fontSize: 12, color: B.gray600 }}>{d.name}</span>
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: B.black }}>{d.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ flex: 0.8, background: B.white, borderRadius: 16, padding: "24px 28px", border: `1px solid ${B.gray200}` }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: B.black, marginBottom: 20, fontFamily: "'Poppins', sans-serif" }}>Pipeline Stages</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {PIPELINE_STAGES.map((stage) => {
              const count = normalizedData.filter((p) => p.status === stage.key).length;
              return (
                <div key={stage.key}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: B.gray600 }}>{stage.label}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: B.black }}>{count}</span>
                  </div>
                  <ProgressBar value={Math.round((count / prospectData.length) * 100)} color={stage.color} />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent activity */}
      {recentActivities.length > 0 && (
        <div style={{ background: B.white, borderRadius: 16, padding: "24px 28px", border: `1px solid ${B.gray200}` }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: B.black, marginBottom: 18, fontFamily: "'Poppins', sans-serif" }}>Recent Activity</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {recentActivities.map((act) => (
              <div key={act.id} style={{ display: "flex", alignItems: "center", gap: 14, paddingBottom: 10, borderBottom: `1px solid ${B.gray100}` }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: B.purplePale, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {act.type === "email" ? <Mail size={15} color={B.purple} /> : act.type === "call" ? <Phone size={15} color={B.success} /> : act.type === "meeting" ? <Calendar size={15} color={B.warning} /> : <FileText size={15} color={B.gray500} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: B.black }}>{act.type.charAt(0).toUpperCase() + act.type.slice(1)} · {act.prospect?.company}</div>
                  <div style={{ fontSize: 12, color: B.gray500 }}>{act.note}</div>
                </div>
                <div style={{ fontSize: 11, color: B.gray400 }}>{new Date(act.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recently contacted */}
      {contacted.length > 0 && (
        <div style={{ background: B.white, borderRadius: 16, padding: "24px 28px", border: `1px solid ${B.gray200}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: B.black, fontFamily: "'Poppins', sans-serif" }}>Recently Contacted</div>
            <button onClick={() => setActiveTab("outreach")} style={{ fontSize: 12, color: B.purple, fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>View All →</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {contacted.map((p) => (
              <div key={p.id} onClick={() => setActiveModal(p)} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", background: B.gray50, borderRadius: 10, cursor: "pointer", transition: "background 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.background = B.purplePale}
                onMouseLeave={e => e.currentTarget.style.background = B.gray50}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: B.purple, display: "flex", alignItems: "center", justifyContent: "center", color: B.white, fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{p.name.charAt(0)}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: B.black }}>{p.name}</div>
                  <div style={{ fontSize: 11, color: B.gray500 }}>{p.title} · {p.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // ─── Pipeline Tab ─────────────────────────────────────────────────────────
  const PipelineTab = () => (
    <div style={{ padding: pad, height: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Poppins', sans-serif", color: B.black }}>Sales Pipeline</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {PIPELINE_STAGES.map((s) => {
            const count = normalizedData.filter(p => p.status === s.key).length;
            return (
              <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 5, background: B.white, border: `1px solid ${B.gray200}`, borderRadius: 8, padding: "6px 12px", fontSize: 12 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: s.color }} />
                <span style={{ color: B.gray600 }}>{s.label}</span>
                <span style={{ fontWeight: 700, color: B.black }}>{count}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", overflowX: "auto", paddingBottom: 12, flex: 1, minHeight: 0 }}>
        {PIPELINE_STAGES.map((stage) => {
          const stageProspects = normalizedData.filter((p) => p.status === stage.key);
          return (
            <div key={stage.key} style={{ flex: "1 1 0", minWidth: 180, height: "100%", display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: B.white, borderRadius: "12px 12px 0 0", border: `1px solid ${B.gray200}`, borderBottom: `3px solid ${stage.color}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: stage.color }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: B.black }}>{stage.label}</span>
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: B.gray500, background: B.gray100, borderRadius: 6, padding: "2px 8px" }}>{stageProspects.length}</span>
              </div>
              <div style={{ background: B.gray50, borderRadius: "0 0 12px 12px", padding: 8, border: `1px solid ${B.gray200}`, borderTop: "none", flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
                {stageProspects.length === 0 ? (
                  <div style={{ textAlign: "center", color: B.gray400, fontSize: 12, padding: "24px 16px" }}>No prospects</div>
                ) : stageProspects.map((p) => {
                  const actCount = getActivities(p.id).length;
                  return (
                    <div key={p.id} onClick={() => setActiveModal(p)} style={{ background: B.white, borderRadius: 10, padding: "14px 16px", border: `1px solid ${B.gray200}`, cursor: "pointer", transition: "box-shadow 0.15s, transform 0.1s" }}
                      onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 12px rgba(113,95,242,0.12)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                      onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}>
                      {(actCount > 0 || p.dealValue) && (
                        <div style={{ display: "flex", gap: 4, alignItems: "center", marginBottom: 6, justifyContent: "flex-end" }}>
                          {actCount > 0 && <span style={{ fontSize: 10, fontWeight: 700, background: B.purplePale, color: B.purple, borderRadius: 5, padding: "2px 6px" }}>{actCount} notes</span>}
                          {p.dealValue && <span style={{ fontSize: 10, fontWeight: 700, background: B.successBg, color: B.success, borderRadius: 5, padding: "2px 6px" }}>${p.dealValue.toLocaleString()}</span>}
                        </div>
                      )}
                      <div style={{ fontSize: 13, fontWeight: 700, color: B.black, marginBottom: 2 }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: B.gray500, marginBottom: 6 }}>{p.company}</div>
                      <div style={{ fontSize: 10, color: B.gray400, background: B.gray100, borderRadius: 5, padding: "2px 7px", display: "inline-block" }}>{p.industry}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ─── Clients Tab ──────────────────────────────────────────────────────────
  const ClientsTab = () => {
    const clients = normalizedData.filter((p) => p.status === "closed");
    const totalClientRevenue = clients.reduce((s, p) => s + (p.dealValue || 0), 0);

    if (clients.length === 0) {
      return (
        <div style={{ padding: pad, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
          <div style={{ width: 72, height: 72, borderRadius: "50%", background: B.purplePale, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
            <Users size={32} color={B.purple} />
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: B.black, fontFamily: "'Poppins', sans-serif", marginBottom: 8 }}>No Clients Yet</div>
          <div style={{ fontSize: 14, color: B.gray500, textAlign: "center", maxWidth: 360, marginBottom: 24 }}>
            Clients appear here once a prospect is marked <strong>Closed Won</strong>. You have <strong>{contacted.length}</strong> contacted and <strong>{normalizedData.filter(p => p.status === "proposal").length}</strong> at proposal stage.
          </div>
          <button onClick={() => setActiveTab("pipeline")} style={{ padding: "11px 24px", background: B.purple, color: B.white, border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>View Pipeline →</button>
        </div>
      );
    }

    return (
      <div style={{ padding: pad, display: "flex", flexDirection: "column", gap: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Poppins', sans-serif", color: B.black }}>Client Management</div>
        </div>

        {/* Metrics */}
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <MetricCard icon={Users} label="Total Clients" value={clients.length} lime />
          <MetricCard icon={DollarSign} label="Total Revenue" value={totalClientRevenue > 0 ? `$${totalClientRevenue.toLocaleString()}` : "$0"} />
          <MetricCard icon={Activity} label="Activities Logged" value={clients.reduce((s, p) => s + getActivities(p.id).length, 0)} />
          <MetricCard icon={Target} label="Industries" value={new Set(clients.map(p => p.industry)).size} />
        </div>

        {/* Client cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 16 }}>
          {clients.map((p) => {
            const svc = SERVICE_TYPES.find(s => s.key === p.serviceType);
            const actCount = getActivities(p.id).length;
            return (
              <div key={p.id} onClick={() => setActiveModal(p)} style={{ background: B.white, borderRadius: 16, padding: "22px 24px", border: `1px solid ${B.gray200}`, cursor: "pointer", transition: "box-shadow 0.15s, transform 0.1s" }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 8px 24px rgba(113,95,242,0.1)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: B.lime, display: "flex", alignItems: "center", justifyContent: "center", color: B.black, fontSize: 16, fontWeight: 800, flexShrink: 0 }}>{p.company.charAt(0)}</div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: B.black, fontFamily: "'Poppins', sans-serif" }}>{p.company}</div>
                      <div style={{ fontSize: 12, color: B.gray500, marginTop: 1 }}>{p.name} · {p.title}</div>
                    </div>
                  </div>
                  <span style={{ background: "#EFFFCC", color: B.limeDark, padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>Client</span>
                </div>

                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                  <span style={{ fontSize: 10, background: B.gray100, color: B.gray500, borderRadius: 5, padding: "2px 7px", fontWeight: 600 }}>{p.industry}</span>
                  {svc && <span style={{ fontSize: 10, background: svc.colorBg, color: svc.color, borderRadius: 5, padding: "2px 7px", fontWeight: 600 }}>{svc.name}</span>}
                  {actCount > 0 && <span style={{ fontSize: 10, background: B.purplePale, color: B.purple, borderRadius: 5, padding: "2px 7px", fontWeight: 600 }}>{actCount} activities</span>}
                </div>

                {p.dealValue ? (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: B.successBg, borderRadius: 8, padding: "10px 14px" }}>
                    <span style={{ fontSize: 12, color: B.success, fontWeight: 600 }}>Contract Value</span>
                    <span style={{ fontSize: 18, fontWeight: 800, color: B.success, fontFamily: "'Poppins', sans-serif" }}>${p.dealValue.toLocaleString()}</span>
                  </div>
                ) : (
                  <div style={{ background: B.gray100, borderRadius: 8, padding: "10px 14px", fontSize: 12, color: B.gray400 }}>No contract value recorded</div>
                )}

                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  {p.email && (
                    <a href={`https://mail.google.com/mail/?view=cm&fs=1&to=${p.email}&su=Revoya Advisory - ${p.company}`} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                      style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", background: B.purplePale, color: B.purple, borderRadius: 8, fontSize: 12, fontWeight: 600, textDecoration: "none" }}>
                      <Mail size={12} /> Email
                    </a>
                  )}
                  {p.linkedin && (
                    <a href={p.linkedin} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                      style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", background: "#EBF5FF", color: "#0A66C2", borderRadius: 8, fontSize: 12, fontWeight: 600, textDecoration: "none" }}>
                      <Linkedin size={12} /> LinkedIn
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ─── Projects Tab ─────────────────────────────────────────────────────────
  const ProjectsTab = () => {
    const active = projectProspects.filter(p => p.status === "proposal");
    const closed = projectProspects.filter(p => p.status === "closed");
    const revenueData = SERVICE_TYPES.map(svc => ({
      name: svc.name.replace("AI ", "").replace(" Assessment", " Assess."),
      value: projectProspects.filter(p => p.serviceType === svc.key).reduce((s, p) => s + (p.dealValue || 0), 0),
    })).filter(d => d.value > 0);

    return (
      <div style={{ padding: pad, display: "flex", flexDirection: "column", gap: 28 }}>
        {/* Header */}
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Poppins', sans-serif", color: B.black }}>Projects</div>
          <div style={{ fontSize: 13, color: B.gray500, marginTop: 4 }}>
            Tracked by service type. Tag a prospect&apos;s service in their Edit Details tab to associate them here.
          </div>
        </div>

        {/* Metrics */}
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <MetricCard icon={Briefcase} label="Active Proposals" value={active.length} />
          <MetricCard icon={Activity} label="Closed Won" value={closed.length} lime />
          <MetricCard icon={DollarSign} label="Pipeline Revenue" value={totalRevenue > 0 ? `$${totalRevenue.toLocaleString()}` : "$0"} />
          <MetricCard icon={Target} label="Companies" value={new Set(projectProspects.map(p => p.company)).size} />
        </div>

        {/* Revenue by service chart */}
        {revenueData.length > 0 && (
          <div style={{ background: B.white, borderRadius: 16, padding: "24px 28px", border: `1px solid ${B.gray200}` }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: B.black, marginBottom: 20, fontFamily: "'Poppins', sans-serif" }}>Revenue by Service</div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={revenueData} barSize={48}>
                <CartesianGrid strokeDasharray="3 3" stroke={B.gray100} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: B.gray500 }} />
                <YAxis tick={{ fontSize: 11, fill: B.gray500 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={v => [`$${v.toLocaleString()}`, "Revenue"]} />
                <Bar dataKey="value" fill={B.purple} radius={[5, 5, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Service type cards — always shown */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {SERVICE_TYPES.map((svc) => {
            const Icon = svc.icon;
            const svcProspects = projectProspects.filter(p => p.serviceType === svc.key);
            const svcActive = svcProspects.filter(p => p.status === "proposal");
            const svcClosed = svcProspects.filter(p => p.status === "closed");
            const svcRevenue = svcProspects.reduce((s, p) => s + (p.dealValue || 0), 0);

            return (
              <div key={svc.key} style={{ background: B.white, borderRadius: 18, border: `1px solid ${B.gray200}`, overflow: "hidden" }}>
                {/* Service header */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 28px", borderBottom: `1px solid ${B.gray200}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: B.purplePale, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Icon size={20} color={B.purple} />
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: B.black, fontFamily: "'Poppins', sans-serif" }}>{svc.name}</div>
                      <div style={{ fontSize: 12, color: B.gray400, marginTop: 2, fontWeight: 500 }}>{svc.engagementType}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 24, textAlign: "right" }}>
                    <div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: B.black, fontFamily: "'Poppins', sans-serif", lineHeight: 1 }}>{svcActive.length}</div>
                      <div style={{ fontSize: 11, color: B.gray400, marginTop: 3 }}>Active</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: B.black, fontFamily: "'Poppins', sans-serif", lineHeight: 1 }}>{svcClosed.length}</div>
                      <div style={{ fontSize: 11, color: B.gray400, marginTop: 3 }}>Closed</div>
                    </div>
                    {svcRevenue > 0 && (
                      <div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: B.purple, fontFamily: "'Poppins', sans-serif", lineHeight: 1 }}>${svcRevenue.toLocaleString()}</div>
                        <div style={{ fontSize: 11, color: B.gray400, marginTop: 3 }}>Revenue</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div style={{ padding: "14px 28px", borderBottom: `1px solid ${B.gray100}`, fontSize: 13, color: B.gray500, lineHeight: 1.7 }}>
                  {svc.description}
                </div>

                {/* Prospect list for this service */}
                {svcProspects.length > 0 ? (
                  <div style={{ padding: "14px 28px 18px", display: "flex", flexDirection: "column", gap: 8 }}>
                    {svcProspects.map(p => (
                      <div key={p.id} onClick={() => setActiveModal(p)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: B.gray50, borderRadius: 10, cursor: "pointer", transition: "background 0.15s" }}
                        onMouseEnter={e => e.currentTarget.style.background = B.purplePale}
                        onMouseLeave={e => e.currentTarget.style.background = B.gray50}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: B.purple, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{p.company.charAt(0)}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: B.black }}>{p.company}</div>
                          <div style={{ fontSize: 11, color: B.gray500 }}>{p.name} · {p.title}</div>
                        </div>
                        <StatusBadge status={p.status} />
                        {p.dealValue && <span style={{ fontSize: 13, fontWeight: 700, color: B.purple }}>${p.dealValue.toLocaleString()}</span>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: "14px 28px 18px", fontSize: 13, color: B.gray400 }}>
                    No prospects assigned yet — open a prospect and set their Service Type to <strong style={{ color: B.gray600 }}>{svc.name}</strong>.
                  </div>
                )}
              </div>
            );
          })}

          {/* Untagged prospects in proposal/closed */}
          {projectProspects.filter(p => !p.serviceType).length > 0 && (
            <div style={{ background: B.white, borderRadius: 18, border: `1px solid ${B.gray200}`, overflow: "hidden" }}>
              <div style={{ padding: "16px 24px", borderBottom: `1px solid ${B.gray100}`, fontSize: 14, fontWeight: 700, color: B.gray500 }}>
                Unassigned — no service type tagged
              </div>
              <div style={{ padding: "12px 24px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
                {projectProspects.filter(p => !p.serviceType).map(p => (
                  <div key={p.id} onClick={() => setActiveModal(p)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: B.gray50, borderRadius: 10, cursor: "pointer", transition: "background 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = B.purplePale}
                    onMouseLeave={e => e.currentTarget.style.background = B.gray50}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: B.gray300, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{p.company.charAt(0)}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: B.black }}>{p.company}</div>
                      <div style={{ fontSize: 11, color: B.gray500 }}>{p.name}</div>
                    </div>
                    <StatusBadge status={p.status} />
                    <span style={{ fontSize: 11, color: B.purple, fontWeight: 600 }}>+ assign service</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─── Outreach Tab ─────────────────────────────────────────────────────────
  const OutreachTab = () => {
    const pageProspects = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    const pageIds = pageProspects.map(p => p.id);
    const allPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.has(id));

    const toggleAll = () => {
      if (allPageSelected) {
        setSelectedIds(prev => { const n = new Set(prev); pageIds.forEach(id => n.delete(id)); return n; });
      } else {
        setSelectedIds(prev => { const n = new Set(prev); pageIds.forEach(id => n.add(id)); return n; });
      }
    };
    const toggleOne = (id) => {
      setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    };

    return (
    <div style={{ padding: pad, display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Poppins', sans-serif", color: B.black }}>Outreach</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: B.gray500 }}>{filtered.length} of {prospectData.length} prospects</span>
            <button
              onClick={() => { setFilterFollowUp(f => !f); setFilterNurture(false); }}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700,
                background: filterFollowUp ? "#FFF3E0" : B.gray50,
                color: filterFollowUp ? B.warning : B.gray500,
                border: `1px solid ${filterFollowUp ? B.warning + "88" : B.gray200}`,
                borderRadius: 6, padding: "3px 10px", cursor: "pointer" }}>
              Follow-ups due
              {filterFollowUp && <X size={10} />}
            </button>
            <button
              onClick={() => { setFilterNurture(f => !f); setFilterFollowUp(false); }}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700,
                background: filterNurture ? B.gray100 : B.gray50,
                color: filterNurture ? B.gray700 : B.gray500,
                border: `1px solid ${filterNurture ? B.gray400 : B.gray200}`,
                borderRadius: 6, padding: "3px 10px", cursor: "pointer" }}>
              Nurture
              {filterNurture && <X size={10} />}
            </button>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <button onClick={() => setNewProspectOpen(true)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: B.purple, color: B.white, border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            <Plus size={14} /> Add Prospect
          </button>
          <button onClick={() => setImportExportOpen(true)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: B.white, color: B.gray600, border: `1px solid ${B.gray200}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            <FileText size={14} /> Import / Export
          </button>
          <div style={{ position: "relative" }}>
            <Search size={14} color={B.gray400} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search prospects…"
              style={{ paddingLeft: 32, paddingRight: 12, paddingTop: 8, paddingBottom: 8, borderRadius: 8, border: `1px solid ${B.gray200}`, fontSize: 13, outline: "none", width: 200, fontFamily: "inherit" }} />
          </div>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${B.gray200}`, fontSize: 12, outline: "none", background: B.white, cursor: "pointer", fontFamily: "inherit" }}>
            <option value="all">All Status</option>
            {PIPELINE_STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select value={filterIndustry} onChange={(e) => setFilterIndustry(e.target.value)} style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${B.gray200}`, fontSize: 12, outline: "none", background: B.white, cursor: "pointer", fontFamily: "inherit" }}>
            <option value="all">All Industries</option>
            {liveIndustries.map((ind) => <option key={ind} value={ind}>{ind}</option>)}
          </select>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", background: B.purplePale, borderRadius: 10, border: `1px solid ${B.purple}33`, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: B.purple }}>{selectedIds.size} selected</span>
          <span style={{ color: B.gray300 }}>|</span>
          <span style={{ fontSize: 12, color: B.gray600, fontWeight: 600 }}>Move to:</span>
          {PIPELINE_STAGES.map(s => (
            <button key={s.key} onClick={() => handleBulkStatusUpdate(s.key)}
              style={{ padding: "5px 12px", borderRadius: 7, background: B.white, border: `1px solid ${B.gray200}`, fontSize: 12, fontWeight: 600, color: B.gray600, cursor: "pointer" }}>
              {s.label}
            </button>
          ))}
          <button onClick={() => setSelectedIds(new Set())} style={{ marginLeft: "auto", padding: "5px 12px", borderRadius: 7, background: "none", border: "none", fontSize: 12, color: B.gray500, cursor: "pointer" }}>
            Clear
          </button>
        </div>
      )}

      <div style={{ background: B.white, borderRadius: 16, border: `1px solid ${B.gray200}`, overflow: "hidden" }}>
        {!isMobile && (
          <div style={{ display: "grid", gridTemplateColumns: "32px 1.8fr 1.8fr 120px 1.4fr 100px", padding: "12px 20px", background: B.gray50, borderBottom: `1px solid ${B.gray200}`, alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              <button onClick={toggleAll} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", padding: 0 }}>
                {allPageSelected ? <CheckSquare size={15} color={B.purple} /> : <Square size={15} color={B.gray400} />}
              </button>
            </div>
            {["Prospect", "Company / Industry", "Status", "Email", "Actions"].map((h) => (
              <div key={h} style={{ fontSize: 11, fontWeight: 700, color: B.gray500, textTransform: "uppercase", letterSpacing: 0.5, paddingLeft: h === "Email" ? 12 : 0 }}>{h}</div>
            ))}
          </div>
        )}
        <div style={{ maxHeight: isMobile ? "none" : "calc(100vh - 300px)", overflowY: "auto" }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: "center", color: B.gray400, fontSize: 13, padding: "40px 20px" }}>No prospects match your filters.</div>
          ) : filterFollowUp ? (
            /* ── Follow-Up Queue View ───────────────────────────────── */
            <div>
              {filtered.map((p, idx) => {
                const touch = getCurrentTouch(p);
                const overdue = getDaysOverdue(p);
                const rowBorder = idx < filtered.length - 1 ? `1px solid ${B.gray100}` : "none";
                const isLinkedIn = touch?.channel === "linkedin";
                return (
                  <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: rowBorder, transition: "background 0.1s" }}
                    onMouseEnter={e => e.currentTarget.style.background = B.gray50}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <div style={{ width: 38, height: 38, borderRadius: "50%", background: B.purplePale, display: "flex", alignItems: "center", justifyContent: "center", color: B.purple, fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{p.name.charAt(0)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: B.black }}>{p.name}</span>
                        <TouchBadge prospect={p} />
                        {overdue && <span style={{ fontSize: 10, fontWeight: 700, background: B.dangerBg, color: B.danger, borderRadius: 5, padding: "2px 7px" }}>{overdue}d overdue</span>}
                        {isLinkedIn && <span style={{ fontSize: 10, fontWeight: 700, background: "#EBF5FF", color: "#0A66C2", borderRadius: 5, padding: "2px 7px" }}>LinkedIn</span>}
                      </div>
                      <div style={{ fontSize: 11, color: B.gray500, marginTop: 2 }}>{p.company}{p.title ? ` · ${p.title}` : ""}</div>
                      <div style={{ fontSize: 11, color: B.gray400, marginTop: 1 }}>{touch?.label || "—"}{touch ? ` · Day ${touch.dayOffset}` : ""}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
                      {isLinkedIn ? (
                        <button onClick={() => setLogTouchTarget(p)} style={{ padding: "6px 14px", borderRadius: 7, background: "#EBF5FF", color: "#0A66C2", border: "1px solid #0A66C233", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Log LinkedIn Touch</button>
                      ) : (
                        <>
                          {p.email && <button onClick={() => { const tc = touch || getCurrentTouch(p); setComposeContext(tc); setComposeProspect(p); setComposeOpen(true); }} style={{ padding: "6px 14px", borderRadius: 7, background: B.purple, color: B.white, border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Send Follow-up</button>}
                          <button onClick={() => setLogTouchTarget(p)} style={{ padding: "6px 12px", borderRadius: 7, background: B.purplePale, color: B.purple, border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Log Touch</button>
                        </>
                      )}
                      <button onClick={() => updateProspect(p.id, { nurture: true, nurtureReactivateDate: (() => { const d = new Date(); d.setDate(d.getDate() + 90); return d.toISOString(); })() })} style={{ padding: "6px 10px", borderRadius: 7, background: B.gray100, color: B.gray500, border: "none", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Skip to Nurture</button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : pageProspects.map((p, idx) => {
            const actCount = getActivities(p.id).length;
            const rowBorder = idx < Math.min(pageSize, filtered.length) - 1 ? `1px solid ${B.gray100}` : "none";
            const isChecked = selectedIds.has(p.id);
            if (isMobile) return (
              <div key={p.id} onClick={() => setActiveModal(p)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderBottom: rowBorder, cursor: "pointer", transition: "background 0.1s" }}
                onMouseEnter={e => e.currentTarget.style.background = B.gray50}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: B.purplePale, display: "flex", alignItems: "center", justifyContent: "center", color: B.purple, fontSize: 14, fontWeight: 700, flexShrink: 0 }}>{p.name.charAt(0)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: B.black }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: B.gray500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.company} · {p.title}</div>
                  <div style={{ display: "flex", gap: 5, marginTop: 5, flexWrap: "wrap" }}>
                    <StatusBadge status={p.status} />
                    <TouchBadge prospect={p} />
                    {actCount > 0 && <span style={{ fontSize: 10, fontWeight: 700, background: B.purplePale, color: B.purple, borderRadius: 5, padding: "2px 6px" }}>{actCount}</span>}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  {p.email && <button onClick={(e) => { e.stopPropagation(); setComposeProspect(p); setComposeOpen(true); }} style={{ width: 32, height: 32, borderRadius: 8, background: B.purplePale, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Mail size={14} color={B.purple} /></button>}
                  <button onClick={(e) => { e.stopPropagation(); setActiveModal(p); }} style={{ width: 32, height: 32, borderRadius: 8, background: B.gray100, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Plus size={14} color={B.gray500} /></button>
                </div>
              </div>
            );
            return (
              <div key={p.id} style={{ display: "grid", gridTemplateColumns: "32px 1.8fr 1.8fr 120px 1.4fr 100px", padding: "14px 20px", alignItems: "center", borderBottom: rowBorder, transition: "background 0.1s", background: isChecked ? B.purplePale : "transparent" }}
                onMouseEnter={e => { if (!isChecked) e.currentTarget.style.background = B.gray50; }}
                onMouseLeave={e => { if (!isChecked) e.currentTarget.style.background = "transparent"; }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <button onClick={(e) => { e.stopPropagation(); toggleOne(p.id); }} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", padding: 0 }}>
                    {isChecked ? <CheckSquare size={15} color={B.purple} /> : <Square size={15} color={B.gray400} />}
                  </button>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: "50%", background: B.purplePale, display: "flex", alignItems: "center", justifyContent: "center", color: B.purple, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{p.name.charAt(0)}</div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: B.black }}>{p.name}</div>
                    {p.title && <div style={{ fontSize: 11, color: B.gray400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>{p.title}</div>}
                  </div>
                  {actCount > 0 && <span style={{ fontSize: 10, fontWeight: 700, background: B.purplePale, color: B.purple, borderRadius: 5, padding: "1px 5px" }}>{actCount}</span>}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: B.black }}>{p.company}</div>
                  {p.industry && <div style={{ fontSize: 11, color: B.gray500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>{p.industry}</div>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}><StatusBadge status={p.status} /><TouchBadge prospect={p} /></div>
                <div style={{ fontSize: 12, color: p.email ? B.gray600 : B.gray400, paddingLeft: 12 }}>
                  {p.email ? <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", maxWidth: 180 }}>{p.email}</span>
                    : <span style={{ color: B.gray300, fontStyle: "italic", cursor: "pointer" }} onClick={() => setActiveModal(p)}>+ add email</span>}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {p.email ? (
                    <button onClick={(e) => { e.stopPropagation(); setComposeProspect(p); setComposeOpen(true); }} style={{ width: 30, height: 30, borderRadius: 7, background: B.purplePale, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                      onMouseEnter={e => e.currentTarget.style.background = B.purple}
                      onMouseLeave={e => e.currentTarget.style.background = B.purplePale}>
                      <Mail size={13} color={B.purple} />
                    </button>
                  ) : (
                    <div style={{ width: 30, height: 30, borderRadius: 7, background: B.gray100, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Mail size={13} color={B.gray300} />
                    </div>
                  )}
                  {p.phone && (
                    <a href={`tel:${p.phone}`} onClick={e => e.stopPropagation()} style={{ width: 30, height: 30, borderRadius: 7, background: B.successBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Phone size={13} color={B.success} />
                    </a>
                  )}
                  <a href={p.linkedin} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ width: 30, height: 30, borderRadius: 7, background: "#EBF5FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Linkedin size={13} color="#0A66C2" />
                  </a>
                  <button onClick={() => setActiveModal(p)} style={{ width: 30, height: 30, borderRadius: 7, background: B.gray100, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Plus size={13} color={B.gray500} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        {/* Pagination */}
        {filtered.length > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: `1px solid ${B.gray100}`, background: B.gray50, flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontSize: 12, color: B.gray500 }}>
              Showing <strong>{Math.min((currentPage - 1) * pageSize + 1, filtered.length)}</strong>–<strong>{Math.min(currentPage * pageSize, filtered.length)}</strong> of <strong>{filtered.length}</strong>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} style={{ padding: "5px 10px", borderRadius: 7, border: `1px solid ${B.gray200}`, fontSize: 12, background: B.white, cursor: "pointer", fontFamily: "inherit", color: B.gray600 }}>
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                style={{ padding: "5px 12px", borderRadius: 7, border: `1px solid ${B.gray200}`, background: currentPage === 1 ? B.gray100 : B.white, color: currentPage === 1 ? B.gray400 : B.gray600, fontSize: 12, cursor: currentPage === 1 ? "default" : "pointer", fontFamily: "inherit" }}>
                ← Prev
              </button>
              {Array.from({ length: Math.ceil(filtered.length / pageSize) }, (_, i) => i + 1).filter(n => Math.abs(n - currentPage) < 3).map(n => (
                <button key={n} onClick={() => setCurrentPage(n)}
                  style={{ width: 30, height: 30, borderRadius: 7, border: `1px solid ${n === currentPage ? B.purple : B.gray200}`, background: n === currentPage ? B.purple : B.white, color: n === currentPage ? B.white : B.gray600, fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: n === currentPage ? 700 : 400 }}>
                  {n}
                </button>
              ))}
              <button onClick={() => setCurrentPage(p => Math.min(Math.ceil(filtered.length / pageSize), p + 1))} disabled={currentPage >= Math.ceil(filtered.length / pageSize)}
                style={{ padding: "5px 12px", borderRadius: 7, border: `1px solid ${B.gray200}`, background: currentPage >= Math.ceil(filtered.length / pageSize) ? B.gray100 : B.white, color: currentPage >= Math.ceil(filtered.length / pageSize) ? B.gray400 : B.gray600, fontSize: 12, cursor: currentPage >= Math.ceil(filtered.length / pageSize) ? "default" : "pointer", fontFamily: "inherit" }}>
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    );
  };

  // ─── Settings Tab ─────────────────────────────────────────────────────────
  const SettingsTab = () => {
    const { changePassword, resetPassword } = useAuth();
    const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
    const [resetSent, setResetSent] = useState(false);
    const [pwMsg, setPwMsg] = useState(null);
    const [pwLoading, setPwLoading] = useState(false);

    const handlePasswordChange = async () => {
      if (!pwForm.current.trim()) { setPwMsg({ type: "error", text: "Please enter your current password." }); return; }
      if (!pwForm.next.trim()) { setPwMsg({ type: "error", text: "New password cannot be empty." }); return; }
      if (pwForm.next !== pwForm.confirm) { setPwMsg({ type: "error", text: "Passwords do not match." }); return; }
      if (pwForm.next.length < 6) { setPwMsg({ type: "error", text: "Password must be at least 6 characters." }); return; }
      setPwLoading(true);
      const result = await changePassword(pwForm.current, pwForm.next);
      setPwLoading(false);
      if (result.ok) {
        setPwMsg({ type: "success", text: "Password updated successfully." });
        setPwForm({ current: "", next: "", confirm: "" });
      } else {
        setPwMsg({ type: "error", text: result.error });
      }
    };

    return (
    <div style={{ padding: pad, display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Poppins', sans-serif", color: B.black }}>Settings</div>

      {/* Top row: Profile + Team side by side */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 20, alignItems: "start" }}>

        {/* Profile + Password */}
        <div style={{ background: B.white, borderRadius: 16, padding: "22px 24px", border: `1px solid ${B.gray200}` }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: B.black, marginBottom: 16, fontFamily: "'Poppins', sans-serif" }}>Your Profile</div>
          {(() => {
            const myMember = teamMembers.find(m => m.email?.toLowerCase() === user?.email?.toLowerCase());
            return (
              <>
                <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 16 }}>
                  <div style={{ width: 48, height: 48, borderRadius: "50%", background: B.purple, display: "flex", alignItems: "center", justifyContent: "center", color: B.white, fontSize: 18, fontWeight: 700, flexShrink: 0 }}>{firstName.charAt(0)}</div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: B.black }}>{fullName}</div>
                    {myMember?.role && <div style={{ fontSize: 12, fontWeight: 600, color: B.purple, marginBottom: 2 }}>{myMember.role}</div>}
                    <div style={{ fontSize: 12, color: B.gray500 }}>{user?.email}</div>
                  </div>
                </div>
              </>
            );
          })()}
          <div style={{ borderTop: `1px solid ${B.gray100}`, paddingTop: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: B.black, marginBottom: 12 }}>Change Password</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <input type="password" placeholder="Current password" value={pwForm.current} onChange={e => { setPwForm(f => ({ ...f, current: e.target.value })); setPwMsg(null); }}
                style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${B.gray200}`, fontSize: 13, fontFamily: "inherit", outline: "none" }} />
              <input type="password" placeholder="New password" value={pwForm.next} onChange={e => { setPwForm(f => ({ ...f, next: e.target.value })); setPwMsg(null); }}
                style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${B.gray200}`, fontSize: 13, fontFamily: "inherit", outline: "none" }} />
              <input type="password" placeholder="Confirm new password" value={pwForm.confirm} onChange={e => { setPwForm(f => ({ ...f, confirm: e.target.value })); setPwMsg(null); }}
                style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${B.gray200}`, fontSize: 13, fontFamily: "inherit", outline: "none" }} />
              {pwMsg && <div style={{ fontSize: 12, color: pwMsg.type === "error" ? B.danger : B.success, fontWeight: 600 }}>{pwMsg.text}</div>}
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <button onClick={handlePasswordChange} disabled={pwLoading}
                  style={{ padding: "8px 18px", borderRadius: 8, background: B.purple, color: B.white, border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  {pwLoading ? "Saving..." : "Update Password"}
                </button>
                <button onClick={async () => { const r = await resetPassword(user?.email); if (r.ok) setResetSent(true); }} disabled={resetSent}
                  style={{ padding: "8px 14px", borderRadius: 8, background: "none", color: B.purple, border: `1px solid ${B.purple}33`, fontSize: 12, fontWeight: 600, cursor: resetSent ? "default" : "pointer" }}>
                  {resetSent ? "Reset email sent ✓" : "Send Reset Email"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Team */}
        <TeamMembersSection user={user} teamMembers={teamMembers} addMember={addMember} removeMember={removeMember} />

        {/* Outreach Templates — left column only */}
        <div style={{ background: B.white, borderRadius: 16, padding: "22px 24px", border: `1px solid ${B.gray200}`, gridColumn: isMobile ? undefined : "1" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: B.black, marginBottom: 12, fontFamily: "'Poppins', sans-serif" }}>Outreach Templates</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
            {templates.map((t, i) => {
              const isDefault = DEFAULT_TEMPLATES.find(d => d.id === t.id);
              return (
                <span key={t.id} style={{ fontSize: 12, fontWeight: 600, background: B.purplePale, color: B.purple, borderRadius: 6, padding: "4px 10px", display: "flex", alignItems: "center", gap: 6 }}>
                  {t.name}
                  {!isDefault && (
                    <button onClick={() => {
                      const updated = templates.filter((_, j) => j !== i);
                      setTemplates(updated);
                      localStorage.setItem("revoya_templates", JSON.stringify(updated));
                    }} style={{ background: "none", border: "none", cursor: "pointer", color: B.purple, opacity: 0.6, padding: 0, display: "flex", lineHeight: 1 }}
                      onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                      onMouseLeave={e => e.currentTarget.style.opacity = "0.6"}>
                      <X size={11} />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
          <div style={{ fontSize: 12, color: B.gray400, marginBottom: 14 }}>Templates auto-fill when composing outreach. Edit the body directly in the compose drawer before sending.</div>
          <button onClick={() => {
            const name = window.prompt("Template name:");
            if (!name) return;
            const newT = { id: `t${Date.now()}`, name, subject: "AI Governance for {{company}}", body: "Hi {{firstName}},\n\n" };
            const updated = [...templates, newT];
            setTemplates(updated);
            localStorage.setItem("revoya_templates", JSON.stringify(updated));
          }} style={{ padding: "7px 14px", borderRadius: 8, background: B.white, color: B.purple, border: `1px solid ${B.purple}`, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>+ New Template</button>
        </div>

      </div>
    </div>
    );
  };


  // ─── Layout ───────────────────────────────────────────────────────────────
  const tabContent = { overview: <OverviewTab />, pipeline: <PipelineTab />, clients: <ClientsTab />, projects: <ProjectsTab />, outreach: <OutreachTab />, settings: <SettingsTab /> };

  // ── Sidebar nav (shared between desktop + mobile overlay) ─────────────────
  const SidebarContents = ({ forMobile }) => (
    <>
      <div style={{ padding: "16px 16px", borderBottom: `1px solid ${B.sidebarBorder}`, display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 64 }}>
        <img src="/1.svg" alt="Revoya" onClick={() => handleTabChange("overview")} style={{ height: 38, objectFit: "contain", maxWidth: 160, cursor: "pointer" }} />
        {forMobile ? (
          <button onClick={() => setMobileNavOpen(false)} style={{ background: "rgba(255,255,255,0.07)", border: "none", borderRadius: 7, width: 28, height: 28, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={14} color="rgba(255,255,255,0.5)" />
          </button>
        ) : (
          <button onClick={() => setCollapsed(!collapsed)} style={{ background: "rgba(255,255,255,0.07)", border: "none", borderRadius: 7, width: 28, height: 28, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <ChevronLeft size={14} color="rgba(255,255,255,0.5)" />
          </button>
        )}
      </div>
      <nav style={{ flex: 1, padding: "12px 8px", overflowY: "auto" }}>
        {navItems.map(({ id, label, icon: Icon }) => {
          const active = activeTab === id;
          return (
            <button key={id} onClick={() => handleTabChange(id)} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "11px 12px", justifyContent: "flex-start", background: active ? "rgba(113,95,242,0.18)" : "transparent", border: `1px solid ${active ? "rgba(113,95,242,0.3)" : "transparent"}`, borderRadius: 10, marginBottom: 3, cursor: "pointer", transition: "background 0.15s" }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.05)"; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}>
              <Icon size={17} color={active ? B.lime : "rgba(255,255,255,0.5)"} />
              <span style={{ fontSize: 13, fontWeight: active ? 700 : 500, color: active ? B.white : "rgba(255,255,255,0.5)", whiteSpace: "nowrap" }}>{label}</span>
              {active && <div style={{ width: 5, height: 5, borderRadius: "50%", background: B.lime, marginLeft: "auto" }} />}
            </button>
          );
        })}
      </nav>
      <div style={{ borderTop: `1px solid ${B.sidebarBorder}`, padding: "14px 12px" }}>
        <button onClick={onLogout} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", justifyContent: "flex-start", background: "rgba(255,255,255,0.05)", border: "none", borderRadius: 8, cursor: "pointer", color: "rgba(255,255,255,0.4)", fontSize: 12, fontWeight: 500, transition: "all 0.15s" }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(223,28,65,0.12)"; e.currentTarget.style.color = "#ff6b84"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.color = "rgba(255,255,255,0.4)"; }}>
          <LogOut size={14} /> Sign Out
        </button>
      </div>
    </>
  );

  if (prospectsLoading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#FAFAFE", fontFamily: "'Inter', sans-serif" }}>
      <div style={{ textAlign: "center" }}>
        <img src="/1.svg" alt="Revoya" style={{ height: 40, marginBottom: 20 }} />
        <div style={{ fontSize: 13, color: "#9896A8" }}>Loading…</div>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: B.gray50, fontFamily: "'Inter', sans-serif" }}>

      {/* ── Desktop sidebar ──────────────────────────────────────────────── */}
      {!isMobile && (
        <div style={{ width: sidebarW, minWidth: sidebarW, background: B.sidebar, borderRight: `1px solid ${B.sidebarBorder}`, display: "flex", flexDirection: "column", transition: "width 0.25s ease, min-width 0.25s ease", overflow: "hidden", zIndex: 10 }}>
          {collapsed ? (
            <>
              <div style={{ padding: "16px 0", borderBottom: `1px solid ${B.sidebarBorder}`, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 64 }}>
                <img src="/2.svg" alt="Revoya" style={{ height: 32, width: 32, objectFit: "contain", filter: "brightness(0) invert(1)" }} />
                <button onClick={() => setCollapsed(false)} style={{ position: "absolute", background: "rgba(255,255,255,0.07)", border: "none", borderRadius: 7, width: 28, height: 28, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: 40 }}>
                  <Menu size={14} color="rgba(255,255,255,0.5)" />
                </button>
              </div>
              <nav style={{ flex: 1, padding: "12px 0", overflowY: "auto", display: "flex", flexDirection: "column", alignItems: "center" }}>
                {navItems.map(({ id, icon: Icon, label }) => {
                  const active = activeTab === id;
                  return (
                    <button key={id} onClick={() => setActiveTab(id)} title={label} style={{ width: 44, height: 44, borderRadius: 10, marginBottom: 4, background: active ? "rgba(113,95,242,0.18)" : "transparent", border: `1px solid ${active ? "rgba(113,95,242,0.3)" : "transparent"}`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s" }}
                      onMouseEnter={e => { if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.05)"; }}
                      onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}>
                      <Icon size={17} color={active ? B.lime : "rgba(255,255,255,0.5)"} />
                    </button>
                  );
                })}
              </nav>
              <div style={{ borderTop: `1px solid ${B.sidebarBorder}`, padding: "12px 0", display: "flex", justifyContent: "center" }}>
                <button onClick={onLogout} title="Sign out" style={{ width: 44, height: 44, borderRadius: 10, background: "rgba(255,255,255,0.05)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.4)", transition: "all 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(223,28,65,0.12)"; e.currentTarget.style.color = "#ff6b84"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.color = "rgba(255,255,255,0.4)"; }}>
                  <LogOut size={14} />
                </button>
              </div>
            </>
          ) : (
            <SidebarContents forMobile={false} />
          )}
        </div>
      )}

      {/* ── Mobile sidebar overlay ───────────────────────────────────────── */}
      {isMobile && mobileNavOpen && (
        <>
          <div style={{ position: "fixed", inset: 0, background: "rgba(7,6,38,0.6)", zIndex: 40 }} onClick={() => setMobileNavOpen(false)} />
          <div style={{ position: "fixed", top: 0, left: 0, bottom: 0, width: 260, background: B.sidebar, zIndex: 50, display: "flex", flexDirection: "column", boxShadow: "4px 0 24px rgba(0,0,0,0.3)" }}>
            <SidebarContents forMobile={true} />
          </div>
        </>
      )}

      {/* ── Main content ──────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
        {/* Topbar */}
        <div style={{ height: 62, background: B.white, borderBottom: `1px solid ${B.gray200}`, display: "flex", alignItems: "center", justifyContent: "space-between", padding: isMobile ? "0 16px" : "0 32px", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {isMobile && (
              <button onClick={() => setMobileNavOpen(true)} style={{ background: B.gray100, border: "none", borderRadius: 8, width: 34, height: 34, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Menu size={16} color={B.gray600} />
              </button>
            )}
            {isMobile && <img src="/1.svg" alt="Revoya" style={{ height: 28, objectFit: "contain" }} />}
            {!isMobile && <div style={{ fontSize: 14, fontWeight: 700, color: B.black, fontFamily: "'Poppins', sans-serif" }}>{navItems.find(n => n.id === activeTab)?.label}</div>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {isMobile && <div style={{ fontSize: 13, fontWeight: 600, color: B.gray600 }}>{navItems.find(n => n.id === activeTab)?.label}</div>}
            <div style={{ position: "relative" }}>
              <button onClick={() => { setNotifOpen(o => !o); setNotifications(prev => prev.map(n => ({ ...n, read: true }))); }}
                style={{ background: B.gray100, border: "none", borderRadius: 8, width: 34, height: 34, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                <Bell size={15} color={B.gray500} />
                {notifications.filter(n => !n.read).length > 0 && (
                  <span style={{ position: "absolute", top: 4, right: 4, width: 8, height: 8, borderRadius: "50%", background: B.danger, border: `2px solid ${B.white}` }} />
                )}
              </button>
              {notifOpen && (
                <>
                  <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setNotifOpen(false)} />
                  <div style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", background: B.white, border: `1px solid ${B.gray200}`, borderRadius: 14, width: 320, zIndex: 100, boxShadow: "0 8px 32px rgba(7,6,38,0.12)", overflow: "hidden" }}>
                    <div style={{ padding: "14px 16px 10px", borderBottom: `1px solid ${B.gray100}`, fontSize: 13, fontWeight: 700, color: B.black }}>Notifications</div>
                    {notifications.length === 0 ? (
                      <div style={{ padding: "20px 16px", fontSize: 13, color: B.gray400, textAlign: "center" }}>No notifications yet</div>
                    ) : notifications.slice(0, 8).map(n => {
                      const handleNotifClick = () => {
                        if (!n.action) return;
                        setNotifOpen(false);
                        if (n.action.type === "compose") {
                          const p = normalizedData.find(x => x.id === n.action.prospectId);
                          if (p) { setComposeProspect(p); setComposeOpen(true); }
                        } else if (n.action.type === "prospect") {
                          const p = normalizedData.find(x => x.id === n.action.prospectId);
                          if (p) setActiveModal(p);
                        } else if (n.action.type === "tab") {
                          handleTabChange(n.action.tab);
                        }
                      };
                      return (
                        <div key={n.id} onClick={n.action ? handleNotifClick : undefined}
                          style={{ padding: "12px 16px", borderBottom: `1px solid ${B.gray100}`, display: "flex", gap: 10, alignItems: "flex-start", cursor: n.action ? "pointer" : "default", transition: "background 0.15s" }}
                          onMouseEnter={e => { if (n.action) e.currentTarget.style.background = B.gray50; }}
                          onMouseLeave={e => { e.currentTarget.style.background = ""; }}>
                          <div style={{ width: 8, height: 8, borderRadius: "50%", background: n.type === "success" ? B.success : n.type === "error" ? B.danger : B.warning, marginTop: 4, flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, color: B.black, lineHeight: 1.5 }}>{n.message}</div>
                            <div style={{ fontSize: 11, color: n.action ? B.purple : B.gray400, marginTop: 2, fontWeight: n.action ? 600 : 400 }}>
                              {n.action ? (n.action.type === "compose" ? "Tap to compose email" : n.action.type === "prospect" ? "Tap to view prospect" : "Tap to navigate") : new Date(n.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {notifications.length > 0 && (
                      <button onClick={() => { setNotifications([]); setNotifOpen(false); }} style={{ width: "100%", padding: "10px", fontSize: 12, color: B.gray400, background: "transparent", border: "none", cursor: "pointer", borderTop: `1px solid ${B.gray100}` }}>
                        Clear all
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
            {/* Profile avatar + dropdown */}
            <div style={{ position: "relative" }}>
              <div onClick={() => setProfileOpen(!profileOpen)} style={{ width: 34, height: 34, borderRadius: "50%", background: B.purple, display: "flex", alignItems: "center", justifyContent: "center", color: B.white, fontSize: 13, fontWeight: 700, cursor: "pointer", userSelect: "none", transition: "opacity 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
                onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
                {firstName.charAt(0)}
              </div>
              {profileOpen && (
                <>
                  {/* Click outside to close */}
                  <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setProfileOpen(false)} />
                  <div style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", background: B.white, border: `1px solid ${B.gray200}`, borderRadius: 14, padding: "6px", width: 220, zIndex: 100, boxShadow: "0 8px 32px rgba(7,6,38,0.12)" }}>
                    <div style={{ padding: "12px 14px 10px", borderBottom: `1px solid ${B.gray100}`, marginBottom: 6 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: B.black }}>{fullName}</div>
                      <div style={{ fontSize: 12, color: B.gray500, marginTop: 2 }}>{user?.email}</div>
                    </div>
                    <button onClick={() => { setProfileOpen(false); setActiveTab("settings"); }} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "9px 14px", borderRadius: 8, border: "none", background: "transparent", cursor: "pointer", fontSize: 13, color: B.gray600, fontFamily: "inherit" }}
                      onMouseEnter={e => e.currentTarget.style.background = B.gray100}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <Settings size={14} /> Settings
                    </button>
                    <button onClick={() => { setProfileOpen(false); onLogout(); }} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "9px 14px", borderRadius: 8, border: "none", background: "transparent", cursor: "pointer", fontSize: 13, color: B.danger, fontFamily: "inherit" }}
                      onMouseEnter={e => e.currentTarget.style.background = B.dangerBg}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <LogOut size={14} /> Sign Out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {tabContent[activeTab]}
        </div>
      </div>

      {/* ── Import/Export Modal ───────────────────────────────────────── */}
      {importExportOpen && <ImportExportModal />}

      {/* ── Import result toast ───────────────────────────────────────── */}
      {importToast && (
        <div style={{ position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)", zIndex: 300, background: importToast.type === "success" ? B.success : importToast.type === "error" ? B.danger : B.warning, color: B.white, padding: "12px 22px", borderRadius: 12, fontSize: 13, fontWeight: 600, boxShadow: "0 8px 24px rgba(7,6,38,0.2)", display: "flex", alignItems: "center", gap: 10, whiteSpace: "nowrap", maxWidth: "90vw", overflow: "hidden", textOverflow: "ellipsis" }}>
          {importToast.message}
          <button onClick={() => setImportToast(null)} style={{ background: "rgba(255,255,255,0.25)", border: "none", borderRadius: 6, width: 22, height: 22, cursor: "pointer", color: B.white, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <X size={12} />
          </button>
        </div>
      )}

      {/* ── New Prospect Modal ────────────────────────────────────────── */}
      {newProspectOpen && (
        <NewProspectModal
          onClose={() => setNewProspectOpen(false)}
          onAdd={addProspect}
          industries={liveIndustries.length ? liveIndustries : INDUSTRIES}
        />
      )}

      {/* ── Template Composer Modal ──────────────────────────────────── */}
      {composeOpen && composeProspect && <TemplateComposerModal />}

      {/* ── Log Touch Modal ───────────────────────────────────────────── */}
      {logTouchTarget && <LogTouchModal />}

      {/* ── Prospect Modal ────────────────────────────────────────────── */}
      {activeModal && (
        <ProspectModal
          prospect={activeModal}
          onClose={() => setActiveModal(null)}
          getActivities={getActivities}
          addActivity={addActivity}
          deleteActivity={deleteActivity}
          user={user}
          onUpdate={(id, updates) => {
            updateProspect(id, updates);
            setActiveModal(prev => ({ ...prev, ...updates }));
          }}
        />
      )}
    </div>
  );
}
